import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { getShippingSettings, saveShippingSettings } from '../config/siteSettings';
import OrderDetailsModal from '../components/OrderDetailsModal';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { getCsrfToken } from '../utils/csrf';

function toSlug(str) {
  return str.toString().toLowerCase()
    .replace(/ş/g, 's').replace(/ç/g, 'c').replace(/ğ/g, 'g')
    .replace(/ü/g, 'u').replace(/ö/g, 'o').replace(/ı/g, 'i').replace(/İ/g, 'i')
    .replace(/[^a-z0-9\s-]/g, '').trim()
    .replace(/\s+/g, '-').replace(/-+/g, '-');
}

function AdminPanel() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [products, setProducts] = useState([]);
  const [users, setUsers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [emailLogs, setEmailLogs] = useState([]);
  const [refundRequests, setRefundRequests] = useState([]);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [shippingSettings, setShippingSettings] = useState(getShippingSettings());
  const [newProduct, setNewProduct] = useState({
    name: '',
    description: '',
    price: '',
    stock: '',
    images: '',
    category: '',
    size: '',
    variant_group_id: '',
    subcategory: '',
    compare_at_price: ''
  });
  const [subcategories, setSubcategories] = useState([]);
  const [newSubcategoryInput, setNewSubcategoryInput] = useState('');
  const [editSubcategories, setEditSubcategories] = useState([]);
  const [editNewSubcategoryInput, setEditNewSubcategoryInput] = useState('');
  const [categories, setCategories] = useState(['Eşarp', 'Şal', 'Fular', 'Twilly']);
  const [newCategoryInput, setNewCategoryInput] = useState('');
  const [newCoupon, setNewCoupon] = useState({
    code: '',
    discountPercent: '',
    validUntil: '',
    minPurchase: '',
    maxUsage: '',
    isActive: true,
    description: '',
    targetUser: '',
    couponType: 'general',
    membersOnly: false,
    singleUse: false,
  });
  const [imagePreview, setImagePreview] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [orderSection, setOrderSection] = useState('active');
  const [orderSearch, setOrderSearch] = useState('');
  const [orderSearchType, setOrderSearchType] = useState('all'); // all, order_number, customer, email
  const [orderDateMode, setOrderDateMode] = useState('none'); // none, single, range
  const [orderDateSingle, setOrderDateSingle] = useState('');
  const [orderDateFrom, setOrderDateFrom] = useState('');
  const [orderDateTo, setOrderDateTo] = useState('');
  const [emailLogSearch, setEmailLogSearch] = useState('');
  const [emailLogSearchType, setEmailLogSearchType] = useState('all'); // all, customer, email, order_number
  const [emailLogDateMode, setEmailLogDateMode] = useState('none'); // none, single, range
  const [emailLogDateSingle, setEmailLogDateSingle] = useState('');
  const [emailLogDateFrom, setEmailLogDateFrom] = useState('');
  const [emailLogDateTo, setEmailLogDateTo] = useState('');
  const [refundSearch, setRefundSearch] = useState('');
  const [refundSearchType, setRefundSearchType] = useState('all'); // all, order_number, customer, email
  const [refundDateMode, setRefundDateMode] = useState('none'); // none, single, range
  const [refundDateSingle, setRefundDateSingle] = useState('');
  const [refundDateFrom, setRefundDateFrom] = useState('');
  const [refundDateTo, setRefundDateTo] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productSection, setProductSection] = useState('inStock');
  const [editProduct, setEditProduct] = useState(null);
  const [productSearch, setProductSearch] = useState('');
  const [productSearchType, setProductSearchType] = useState('all');
  const [productStockSort, setProductStockSort] = useState('none');
  const [productPage, setProductPage] = useState(1);
  const PRODUCTS_PER_PAGE = 15;
  const [dashboardStats, setDashboardStats] = useState(null);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [bulkDiscountPercent, setBulkDiscountPercent] = useState('');
  const [bulkDiscountPrice, setBulkDiscountPrice] = useState('');
  const [bulkDiscountLoading, setBulkDiscountLoading] = useState(false);
  const [categoryDiscountCategory, setCategoryDiscountCategory] = useState('');
  const [categoryDiscountPercent, setCategoryDiscountPercent] = useState('');
  const [categoryDiscountPrice, setCategoryDiscountPrice] = useState('');
  const [categoryDiscountLoading, setCategoryDiscountLoading] = useState(false);

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/');
    }
    fetchProducts();
    fetchUsers();
    fetchOrders();
    fetchCoupons();
    fetchEmailLogs();
    fetchRefundRequests();
    fetchDashboardStats();
    fetchShippingSettings();
  }, [user, navigate]);

  const fetchProducts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/products`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setProducts(data);
      }
    } catch (error) {
      console.error('Ürünler yüklenemedi:', error);
    }
  };

  const fetchSubcategories = async (category) => {
    if (!category) { setSubcategories([]); return; }
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/subcategories?category=${encodeURIComponent(category)}`);
      if (response.ok) setSubcategories(await response.json());
    } catch { setSubcategories([]); }
  };

  const fetchEditSubcategories = async (category) => {
    if (!category) { setEditSubcategories([]); return; }
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/subcategories?category=${encodeURIComponent(category)}`);
      if (response.ok) setEditSubcategories(await response.json());
    } catch { setEditSubcategories([]); }
  };

  const addSubcategory = async (category, name, isEdit = false) => {
    if (!name.trim() || !category) return null;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/subcategories`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ category, name: name.trim() })
      });
      if (response.ok) {
        const created = await response.json();
        if (isEdit) {
          setEditSubcategories(prev => [...prev.filter(s => s.id !== created.id), created].sort((a,b) => a.name.localeCompare(b.name)));
          setEditNewSubcategoryInput('');
          return created.name;
        } else {
          setSubcategories(prev => [...prev.filter(s => s.id !== created.id), created].sort((a,b) => a.name.localeCompare(b.name)));
          setNewSubcategoryInput('');
          return created.name;
        }
      }
    } catch { }
    return null;
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (error) {
      console.error('Kullanıcılar yüklenemedi:', error);
      setUsers([
        { id: 1, name: 'Admin', email: 'admin@ipekaksesuar.com', role: 'admin', lastPurchase: '2024-01-15' },
        { id: 2, name: 'Test', email: 'test@test.com', role: 'user', lastPurchase: '2023-12-01' },
        { id: 3, name: 'Eski Müşteri', email: 'eski@test.com', role: 'user', lastPurchase: '2023-06-15' }
      ]);
    }
  };

  const fetchOrders = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/orders`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        console.log('Admin Orders Data:', data); // Debug: Kupon bilgilerini görmek için
        setOrders(data);
      }
    } catch (error) {
      console.error('Siparişler yüklenemedi:', error);
      setOrders([]);
    }
  };

  const fetchDashboardStats = async () => {
    setDashboardLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/dashboard-stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setDashboardStats(await res.json());
    } catch (error) {
      console.error('Dashboard stats yüklenemedi:', error);
    }
    setDashboardLoading(false);
  };

  const fetchCoupons = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/discount-codes`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCoupons(data);
      }
    } catch (error) {
      console.error('Kuponlar yüklenemedi:', error);
    }
  };

  const fetchShippingSettings = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/settings/shipping`);
      if (response.ok) {
        const data = await response.json();
        setShippingSettings(prev => ({ ...prev, fee: data.fee, freeShippingThreshold: data.freeShippingThreshold }));
      }
    } catch (error) {
      console.error('Kargo ayarları yüklenemedi:', error);
    }
  };

  const handleUpdateOrderStatus = async (orderId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/orders/${orderId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        alert('Sipariş durumu güncellendi!');
        fetchOrders(); // Refresh orders list
      } else {
        const error = await response.json();
        alert(`Hata: ${error.error || 'Durum güncellenemedi'}`);
      }
    } catch (error) {
      console.error('Sipariş durumu güncellenemedi:', error);
      alert('Sipariş durumu güncellenirken hata oluştu');
    }
  };

  const handleAddTrackingInfo = async (orderId) => {
    const trackingNumber = prompt('Kargo takip numarasını girin:');
    const cargoCompany = prompt('Kargo şirketini girin (örn: Aras Kargo, Yurtiçi):');

    if (!trackingNumber || !cargoCompany) {
      alert('Takip numarası ve kargo şirketi gerekli!');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/orders/${orderId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          status: 'shipped',
          trackingNumber,
          cargoCompany
        })
      });

      if (response.ok) {
        alert('Kargo bilgileri eklendi ve sipariş kargoya verildi!');
        fetchOrders();
      } else {
        const error = await response.json();
        alert(`Hata: ${error.error || 'Bilgiler eklenemedi'}`);
      }
    } catch (error) {
      console.error('Kargo bilgileri eklenemedi:', error);
      alert('Kargo bilgileri eklenirken hata oluştu');
    }
  };

  const fetchEmailLogs = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/email-logs`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setEmailLogs(data);
      }
    } catch (error) {
      console.error('Email logları yüklenemedi:', error);
      setEmailLogs([]);
    }
  };

  const fetchRefundRequests = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/refund-requests`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setRefundRequests(data);
      }
    } catch (error) {
      console.error('İade talepleri yüklenemedi:', error);
      setRefundRequests([]);
    }
  };

  const handleUpdateRefundRequest = async (requestId, status, adminNotes) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/refund-requests/${requestId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status, adminNotes })
      });

      if (response.ok) {
        alert('İade talebi güncellendi');
        fetchRefundRequests();
        fetchOrders(); // Sipariş durumunu güncelle
      } else {
        const error = await response.json();
        alert(`Hata: ${error.error || 'Güncelleme başarısız'}`);
      }
    } catch (error) {
      console.error('İade talebi güncellenemedi:', error);
      alert('Bir hata oluştu');
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'delivered': return '#28a745';
      case 'shipped': return '#17a2b8';
      case 'preparing': return '#ffc107';
      case 'payment_received': return '#007bff';
      case 'pending': return '#6c757d';
      case 'cancelled': return '#dc3545';
      case 'refunded': return '#6c757d';
      default: return '#6c757d';
    }
  };

  const getStatusText = (status) => {
    switch(status) {
      case 'delivered': return 'Teslim Edildi';
      case 'shipped': return 'Kargoda';
      case 'preparing': return 'Hazırlanıyor';
      case 'payment_received': return 'Ödeme Alındı';
      case 'pending': return 'Beklemede';
      case 'cancelled': return 'İptal Edildi';
      case 'refunded': return 'İade Edildi';
      default: return 'Bilinmeyen';
    }
  };

  const handleAddProduct = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const csrfToken = await getCsrfToken();
      const imageUrls = newProduct.images.split(',').map(url => url.trim()).filter(url => url);

      const productData = {
        ...newProduct,
        price: parseFloat(newProduct.price),
        stock: parseInt(newProduct.stock),
        images: imageUrls,
        compare_at_price: newProduct.compare_at_price ? parseFloat(newProduct.compare_at_price) : null
      };

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/products`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify(productData)
      });
      
      if (response.ok) {
        alert('Ürün eklendi!');
        fetchProducts();
        setNewProduct({ name: '', description: '', price: '', stock: '', images: '', category: '', size: '', variant_group_id: '', subcategory: '', compare_at_price: '' });
        setSubcategories([]);
        setNewSubcategoryInput('');
        setImagePreview('');
      }
    } catch (error) {
      alert('Ürün eklenirken hata oluştu');
    }
  };

  const handleAddCoupon = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/discount-codes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          code: newCoupon.code.toUpperCase(),
          discountPercent: parseInt(newCoupon.discountPercent),
          validUntil: newCoupon.validUntil,
          membersOnly: newCoupon.membersOnly,
          singleUse: newCoupon.singleUse,
        })
      });
      if (response.ok) {
        alert('Kupon oluşturuldu!');
        setNewCoupon({ code: '', discountPercent: '', validUntil: '', minPurchase: '', maxUsage: '', isActive: true, description: '', targetUser: '', couponType: 'general', membersOnly: false, singleUse: false });
        fetchCoupons();
      } else {
        const err = await response.json();
        alert(err.error || 'Kupon oluşturma hatası');
      }
    } catch (error) {
      alert('Bağlantı hatası');
    }
  };

  const deleteCoupon = async (couponId) => {
    if (window.confirm('Bu kuponu silmek istediğinize emin misiniz?')) {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/discount-codes/${couponId}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          fetchCoupons();
        } else {
          alert('Silme hatası');
        }
      } catch (error) {
        alert('Bağlantı hatası');
      }
    }
  };

  // Kargo ayarlarını kaydet
  const handleSaveShippingSettings = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/settings/shipping`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          fee: shippingSettings.fee,
          freeShippingThreshold: shippingSettings.freeShippingThreshold
        })
      });
      if (response.ok) {
        saveShippingSettings(shippingSettings); // localStorage'ı da güncelle (frontend cache)
        alert('Kargo ayarları kaydedildi!');
      } else {
        alert('Kargo ayarları kaydedilemedi');
      }
    } catch (error) {
      alert('Bağlantı hatası');
    }
  };

  const toggleProductVisibility = async (product) => {
    try {
      const token = localStorage.getItem('token');
      const newValue = product.is_active === false ? true : false;
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/products/${product.id}/visibility`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ is_active: newValue }),
      });
      if (response.ok) {
        fetchProducts();
      } else {
        const err = await response.json().catch(() => ({}));
        alert('Hata: ' + (err.error || response.status));
      }
    } catch (error) {
      alert('Sunucu hatası: ' + error.message);
    }
  };

  const handleUpdateProduct = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const csrfToken = await getCsrfToken();
      const imageUrls = typeof editProduct.images === 'string'
        ? editProduct.images.split(',').map(u => u.trim()).filter(u => u)
        : editProduct.images;
      const body = {
        name: editProduct.name,
        description: editProduct.description,
        price: parseFloat(editProduct.price),
        stock: parseInt(editProduct.stock),
        images: imageUrls,
        category: editProduct.category,
        size: editProduct.size,
        variant_group_id: editProduct.variant_group_id ? parseInt(editProduct.variant_group_id) : null,
        subcategory: editProduct.subcategory || null,
        compare_at_price: editProduct.compare_at_price ? parseFloat(editProduct.compare_at_price) : null,
      };
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/products/${editProduct.id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}`, 'X-CSRF-Token': csrfToken },
        body: JSON.stringify(body),
      });
      if (response.ok) {
        alert('Ürün güncellendi!');
        setEditProduct(null);
        fetchProducts();
      } else {
        const err = await response.json();
        alert('Hata: ' + (err.message || 'Güncelleme başarısız'));
      }
    } catch (error) {
      console.error('Ürün güncellenemedi:', error);
      alert('Sunucu hatası');
    }
  };

  // Ortak toplu indirim çağrısı (çoklu seçim ve kategori araçları bunu kullanır)
  const applyBulkDiscount = async (productIds, { percent, newPrice, remove } = {}) => {
    if (!productIds || productIds.length === 0) {
      alert('Ürün seçilmedi');
      return false;
    }
    if (!remove && !percent && !newPrice) {
      alert('Yüzde veya yeni fiyat girin');
      return false;
    }
    try {
      const token = localStorage.getItem('token');
      const csrfToken = await getCsrfToken();
      const body = { productIds };
      if (remove) body.remove = true;
      else if (percent) body.percent = parseFloat(percent);
      else if (newPrice) body.newPrice = parseFloat(newPrice);

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/products/bulk-discount`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify(body)
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        alert(data.message || 'İşlem tamamlandı');
        fetchProducts();
        return true;
      } else {
        alert('Hata: ' + (data.error || 'İndirim uygulanamadı'));
        return false;
      }
    } catch (error) {
      alert('Sunucu hatası: ' + error.message);
      return false;
    }
  };

  const toggleSelectProduct = (id) => {
    setSelectedProductIds(prev => prev.includes(id) ? prev.filter(pid => pid !== id) : [...prev, id]);
  };

  const toggleSelectAllProducts = () => {
    const idsOnPage = paginatedProducts.map(p => p.id);
    const allSelected = idsOnPage.every(id => selectedProductIds.includes(id));
    if (allSelected) {
      setSelectedProductIds(prev => prev.filter(id => !idsOnPage.includes(id)));
    } else {
      setSelectedProductIds(prev => Array.from(new Set([...prev, ...idsOnPage])));
    }
  };

  const handleApplySelectedDiscount = async () => {
    setBulkDiscountLoading(true);
    const ok = await applyBulkDiscount(selectedProductIds, { percent: bulkDiscountPercent, newPrice: bulkDiscountPrice });
    setBulkDiscountLoading(false);
    if (ok) {
      setSelectedProductIds([]);
      setBulkDiscountPercent('');
      setBulkDiscountPrice('');
    }
  };

  const handleRemoveSelectedDiscount = async () => {
    setBulkDiscountLoading(true);
    const ok = await applyBulkDiscount(selectedProductIds, { remove: true });
    setBulkDiscountLoading(false);
    if (ok) {
      setSelectedProductIds([]);
      setBulkDiscountPercent('');
      setBulkDiscountPrice('');
    }
  };

  const handleApplyCategoryDiscount = async () => {
    const ids = products.filter(p => p.category === categoryDiscountCategory).map(p => p.id);
    setCategoryDiscountLoading(true);
    const ok = await applyBulkDiscount(ids, { percent: categoryDiscountPercent, newPrice: categoryDiscountPrice });
    setCategoryDiscountLoading(false);
    if (ok) {
      setCategoryDiscountPercent('');
      setCategoryDiscountPrice('');
    }
  };

  const handleRemoveCategoryDiscount = async () => {
    const ids = products.filter(p => p.category === categoryDiscountCategory).map(p => p.id);
    setCategoryDiscountLoading(true);
    const ok = await applyBulkDiscount(ids, { remove: true });
    setCategoryDiscountLoading(false);
    if (ok) {
      setCategoryDiscountPercent('');
      setCategoryDiscountPrice('');
    }
  };

  const deleteProduct = async (id) => {
    if (window.confirm('Bu ürünü silmek istediğinize emin misiniz?')) {
      try {
        const token = localStorage.getItem('token');
        const csrfToken = await getCsrfToken();
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/products/${id}`, {
          method: 'DELETE',
          credentials: 'include',
          headers: { 'Authorization': `Bearer ${token}`, 'X-CSRF-Token': csrfToken }
        });
        
        if (response.ok) {
          alert('Ürün silindi!');
          fetchProducts();
        }
      } catch (error) {
        alert('Ürün silinirken hata oluştu');
      }
    }
  };

  const handleImageUrlChange = (value) => {
    setNewProduct({ ...newProduct, images: value });
    const firstUrl = value.split(',')[0].trim();
    setImagePreview(firstUrl);
  };

  // Uzun süredir alışveriş yapmayan kullanıcıları bul
  const getInactiveUsers = () => {
    const threeMonthsAgo = new Date();
    threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
    
    return users.filter(user => {
      if (!user.lastPurchase) return true;
      return new Date(user.lastPurchase) < threeMonthsAgo;
    });
  };

  const generateComebackCoupon = (targetUser) => {
    const code = `COMEBACK${targetUser.id}${Date.now().toString().slice(-4)}`;
    setNewCoupon({
      code: code,
      discountPercent: '20',
      validUntil: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 gün
      minPurchase: '100',
      maxUsage: '1',
      isActive: true,
      description: `${targetUser.name} için geri dönüş kuponu`,
      targetUser: targetUser.email,
      couponType: 'comeback'
    });
  };

  const sidebarStyle = {
    width: '200px',
    backgroundColor: '#2c3e50',
    color: 'white',
    padding: '20px',
    minHeight: '100vh'
  };

  const contentStyle = {
    flex: 1,
    padding: '20px',
    backgroundColor: '#f8f9fa',
    overflowY: 'auto'
  };

  const tabStyle = (isActive) => ({
    padding: '10px',
    cursor: 'pointer',
    backgroundColor: isActive ? '#34495e' : 'transparent',
    borderRadius: '5px',
    marginBottom: '5px'
  });

  // Ürün listesi pagination hesaplamaları
  const filteredProducts = products.filter(p => {
    if (productSection === 'hidden') { if (p.is_active !== false) return false; }
    else { if (p.is_active === false) return false; }
    if (productSection === 'inStock' && (p.stock || 0) === 0) return false;
    if (productSection === 'outOfStock' && (p.stock || 0) > 0) return false;
    const q = productSearch.trim().toLowerCase();
    if (!q) return true;
    if (productSearchType === 'id') return String(p.id) === q;
    if (productSearchType === 'variant_group_id') return String(p.variant_group_id || '') === q;
    if (productSearchType === 'size') return (p.size || '').toLowerCase().includes(q);
    if (productSearchType === 'name') return (p.name || '').toLowerCase().includes(q);
    if (productSearchType === 'category') return (p.category || '').toLowerCase().includes(q);
    return (
      String(p.id) === q ||
      String(p.variant_group_id || '').includes(q) ||
      (p.size || '').toLowerCase().includes(q) ||
      (p.name || '').toLowerCase().includes(q) ||
      (p.category || '').toLowerCase().includes(q)
    );
  }).sort((a, b) => {
    if (productStockSort === 'asc') return (a.stock || 0) - (b.stock || 0);
    if (productStockSort === 'desc') return (b.stock || 0) - (a.stock || 0);
    return 0;
  });
  const productTotalPages = Math.ceil(filteredProducts.length / PRODUCTS_PER_PAGE);
  const paginatedProducts = filteredProducts.slice((productPage - 1) * PRODUCTS_PER_PAGE, productPage * PRODUCTS_PER_PAGE);

  return (
    <div style={{ display: 'flex', minHeight: '100vh' }}>
      {/* Sidebar */}
      <div style={sidebarStyle}>
        <h2>Admin Panel</h2>
        <hr style={{ margin: '20px 0' }} />
        <div style={tabStyle(activeTab === 'dashboard')} onClick={() => setActiveTab('dashboard')}>
          📊 Dashboard
        </div>
        <div style={tabStyle(activeTab === 'addProduct')} onClick={() => setActiveTab('addProduct')}>
          ➕ Ürün Ekleme
        </div>
        <div style={tabStyle(activeTab === 'productList')} onClick={() => setActiveTab('productList')}>
          📦 Ürünler
        </div>
        <div style={tabStyle(activeTab === 'coupons')} onClick={() => setActiveTab('coupons')}>
          🎟️ Kupon Yönetimi
        </div>
        <div style={tabStyle(activeTab === 'shipping')} onClick={() => setActiveTab('shipping')}>
          🚚 Kargo Ayarları
        </div>
        <div style={tabStyle(activeTab === 'users')} onClick={() => setActiveTab('users')}>
          👥 Kullanıcılar
        </div>
        <div style={tabStyle(activeTab === 'orders')} onClick={() => setActiveTab('orders')}>
          🛒 Siparişler
        </div>
        <div style={tabStyle(activeTab === 'emailLogs')} onClick={() => setActiveTab('emailLogs')}>
          📧 Email Logları
        </div>
        <div style={tabStyle(activeTab === 'refundRequests')} onClick={() => setActiveTab('refundRequests')}>
          ↩️ İade Talepleri
        </div>
      </div>

      {/* Content */}
      <div style={contentStyle}>
        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <div style={{ maxWidth: '1200px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
              <h1 style={{ margin: 0 }}>Dashboard</h1>
              <button onClick={fetchDashboardStats} style={{ padding: '8px 16px', border: '1px solid #ddd', borderRadius: '6px', background: '#fff', cursor: 'pointer', fontSize: '13px' }}>
                🔄 Yenile
              </button>
            </div>

            {dashboardLoading && <div style={{ textAlign: 'center', padding: '60px', color: '#999' }}>Yükleniyor...</div>}

            {!dashboardLoading && dashboardStats && (() => {
              const s = dashboardStats;
              const card = (title, value, color, sub) => (
                <div style={{ background: '#fff', borderRadius: '10px', padding: '20px 24px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderLeft: `4px solid ${color}` }}>
                  <div style={{ fontSize: '12px', color: '#999', fontWeight: 600, letterSpacing: '0.5px', textTransform: 'uppercase', marginBottom: '8px' }}>{title}</div>
                  <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#111', lineHeight: 1 }}>{value}</div>
                  {sub && <div style={{ fontSize: '12px', color: '#aaa', marginTop: '6px' }}>{sub}</div>}
                </div>
              );
              const statusColors = { pending: '#6c757d', payment_received: '#007bff', preparing: '#ffc107', shipped: '#17a2b8', delivered: '#28a745', cancelled: '#dc3545', refunded: '#6c757d' };
              const statusLabels = { pending: 'Beklemede', payment_received: 'Ödeme Alındı', preparing: 'Hazırlanıyor', shipped: 'Kargoda', delivered: 'Teslim Edildi', cancelled: 'İptal', refunded: 'İade' };
              const totalRevenueFmt = Number(s.orders.totalRevenue).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
              const pendingCount = (s.orders.byStatus['pending'] || 0) + (s.orders.byStatus['payment_received'] || 0);

              return (
                <>
                  {/* Bölüm 1: Özet kartlar */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' }}>
                    {card('Bugünkü Sipariş', s.orders.today, '#667eea')}
                    {card('Bu Ay Sipariş', s.orders.thisMonth, '#27ae60')}
                    {card('Toplam Ciro', `₺${totalRevenueFmt}`, '#f39c12', 'İptal/iade hariç')}
                    {card('Bekleyen Sipariş', pendingCount, '#e74c3c', 'Onay bekliyor')}
                  </div>

                  {/* Bölüm 2: Sipariş durum kartları */}
                  <div style={{ background: '#fff', borderRadius: '10px', padding: '20px 24px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', marginBottom: '24px' }}>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: '#555', marginBottom: '16px' }}>SİPARİŞ DURUMLARI</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '12px' }}>
                      {['pending','payment_received','preparing','shipped','delivered','cancelled','refunded'].map(st => (
                        <div key={st} style={{ textAlign: 'center', padding: '12px 8px', borderRadius: '8px', background: '#f8f9fa' }}>
                          <div style={{ fontSize: '24px', fontWeight: 'bold', color: statusColors[st] }}>{s.orders.byStatus[st] || 0}</div>
                          <div style={{ fontSize: '11px', color: '#888', marginTop: '4px', lineHeight: 1.3 }}>{statusLabels[st]}</div>
                        </div>
                      ))}
                    </div>
                    {s.pendingRefunds > 0 && (
                      <div style={{ marginTop: '12px', padding: '8px 14px', background: '#fff3cd', borderRadius: '6px', fontSize: '13px', color: '#856404' }}>
                        ⚠️ <strong>{s.pendingRefunds}</strong> bekleyen iade talebi var
                      </div>
                    )}
                  </div>

                  {/* Bölüm 3: Grafik + En çok satanlar */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
                    <div style={{ background: '#fff', borderRadius: '10px', padding: '20px 24px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: '#555', marginBottom: '16px' }}>SON 7 GÜN SİPARİŞ TRENDİ</div>
                      <ResponsiveContainer width="100%" height={200}>
                        <BarChart data={s.last7Days} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                          <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#999' }} />
                          <YAxis tick={{ fontSize: 11, fill: '#999' }} allowDecimals={false} />
                          <Tooltip
                            formatter={(value, name) => name === 'count' ? [value, 'Sipariş'] : [`₺${Number(value).toLocaleString('tr-TR')}`, 'Ciro']}
                            labelStyle={{ color: '#333', fontSize: 12 }}
                            contentStyle={{ fontSize: 12, borderRadius: 6 }}
                          />
                          <Bar dataKey="count" fill="#667eea" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>

                    <div style={{ background: '#fff', borderRadius: '10px', padding: '20px 24px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: '#555', marginBottom: '16px' }}>EN ÇOK SATAN ÜRÜNLER</div>
                      {s.topProducts.length === 0 ? (
                        <div style={{ color: '#bbb', fontSize: '13px', paddingTop: '20px' }}>Henüz sipariş yok</div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                          {s.topProducts.map((p, i) => (
                            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                              <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: i === 0 ? '#f39c12' : '#e9ecef', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 700, color: i === 0 ? '#fff' : '#666', flexShrink: 0 }}>{i + 1}</div>
                              <div style={{ flex: 1, fontSize: '13px', color: '#333', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</div>
                              <div style={{ fontSize: '13px', fontWeight: 600, color: '#667eea', flexShrink: 0 }}>{p.total_sold} adet</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Bölüm 4: Ürün metrikleri */}
                  <div style={{ background: '#fff', borderRadius: '10px', padding: '20px 24px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', marginBottom: '24px' }}>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: '#555', marginBottom: '16px' }}>ÜRÜN DURUMU</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '12px' }}>
                      {[
                        { label: 'Toplam Ürün', value: s.products.total, color: '#667eea' },
                        { label: 'Stokta', value: s.products.inStock, color: '#28a745' },
                        { label: 'Stoğu Biten', value: s.products.outOfStock, color: '#dc3545' },
                        { label: 'Gizlenen', value: s.products.hidden, color: '#6c757d' },
                        { label: '⚠️ Az Stok (<5)', value: s.lowStockProducts.length, color: '#f39c12' },
                      ].map((item, i) => (
                        <div key={i} style={{ textAlign: 'center', padding: '14px 8px', borderRadius: '8px', background: '#f8f9fa' }}>
                          <div style={{ fontSize: '26px', fontWeight: 'bold', color: item.color }}>{item.value}</div>
                          <div style={{ fontSize: '11px', color: '#888', marginTop: '4px' }}>{item.label}</div>
                        </div>
                      ))}
                    </div>
                    {s.lowStockProducts.length > 0 && (
                      <div style={{ marginTop: '14px' }}>
                        <div style={{ fontSize: '12px', color: '#999', marginBottom: '8px', fontWeight: 600 }}>STOK UYARISI</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                          {s.lowStockProducts.map(p => (
                            <div key={p.id} style={{ padding: '5px 12px', background: '#fff3cd', borderRadius: '20px', fontSize: '12px', color: '#856404' }}>
                              {p.name} — <strong>{p.stock} adet</strong>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Bölüm 5: Son siparişler + Son kullanıcılar */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div style={{ background: '#fff', borderRadius: '10px', padding: '20px 24px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: '#555', marginBottom: '16px' }}>SON SİPARİŞLER</div>
                      {s.recentOrders.length === 0 ? (
                        <div style={{ color: '#bbb', fontSize: '13px' }}>Henüz sipariş yok</div>
                      ) : (
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                          <thead>
                            <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                              {['Sipariş No', 'Müşteri', 'Tutar', 'Durum'].map(h => (
                                <th key={h} style={{ textAlign: 'left', padding: '6px 8px', color: '#aaa', fontWeight: 600 }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {s.recentOrders.map(o => (
                              <tr key={o.id} style={{ borderBottom: '1px solid #f8f9fa' }}>
                                <td style={{ padding: '8px 8px', color: '#667eea', fontWeight: 600 }}>{o.order_number || `#${o.id}`}</td>
                                <td style={{ padding: '8px 8px', color: '#444', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100px' }}>{o.user_name || '-'}</td>
                                <td style={{ padding: '8px 8px', color: '#333' }}>₺{Number(o.total_price).toLocaleString('tr-TR')}</td>
                                <td style={{ padding: '8px 8px' }}>
                                  <span style={{ padding: '2px 8px', borderRadius: '12px', background: `${statusColors[o.status]}22`, color: statusColors[o.status], fontWeight: 600, fontSize: '11px' }}>
                                    {statusLabels[o.status] || o.status}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>

                    <div style={{ background: '#fff', borderRadius: '10px', padding: '20px 24px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: '#555', marginBottom: '16px' }}>SON KAYIT OLAN KULLANICILAR</div>
                      {s.recentUsers.length === 0 ? (
                        <div style={{ color: '#bbb', fontSize: '13px' }}>Henüz kullanıcı yok</div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                          {s.recentUsers.map(u => (
                            <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '8px 0', borderBottom: '1px solid #f8f9fa' }}>
                              <div style={{ width: '34px', height: '34px', borderRadius: '50%', background: '#667eea22', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: 700, color: '#667eea', flexShrink: 0 }}>
                                {(u.name || u.email || '?')[0].toUpperCase()}
                              </div>
                              <div style={{ flex: 1, overflow: 'hidden' }}>
                                <div style={{ fontSize: '13px', fontWeight: 600, color: '#333', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.name || '-'}</div>
                                <div style={{ fontSize: '11px', color: '#aaa', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.email}</div>
                              </div>
                              <div style={{ fontSize: '11px', color: '#bbb', flexShrink: 0 }}>{new Date(u.created_at).toLocaleDateString('tr-TR')}</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
        )}

        {/* Kupon Yönetimi Tab */}
        {activeTab === 'coupons' && (
          <div>
            <h1>Kupon Yönetimi</h1>
            
            {/* Yeni Kupon Oluştur */}
            <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '10px', marginBottom: '20px' }}>
              <h3>Yeni Kupon Oluştur</h3>
              <form onSubmit={handleAddCoupon}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: 'bold' }}>
                      Kupon Tipi
                    </label>
                    <select
                      value={newCoupon.couponType}
                      onChange={(e) => setNewCoupon({ ...newCoupon, couponType: e.target.value })}
                      style={{ width: '100%', padding: '10px', borderRadius: '5px', border: '1px solid #ddd' }}
                      required
                    >
                      <option value="general">Genel Kupon</option>
                      <option value="personal">Kişiye Özel</option>
                      <option value="comeback">Geri Dönüş Kuponu</option>
                    </select>
                  </div>
                  
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: 'bold' }}>
                      Kupon Kodu
                    </label>
                    <input
                      type="text"
                      placeholder="Örn: INDIRIM20"
                      value={newCoupon.code}
                      onChange={(e) => setNewCoupon({ ...newCoupon, code: e.target.value })}
                      style={{ width: '100%', padding: '10px', borderRadius: '5px', border: '1px solid #ddd' }}
                      required
                    />
                  </div>
                  
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: 'bold' }}>
                      İndirim Yüzdesi (%)
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="100"
                      placeholder="20"
                      value={newCoupon.discountPercent}
                      onChange={(e) => setNewCoupon({ ...newCoupon, discountPercent: e.target.value })}
                      style={{ width: '100%', padding: '10px', borderRadius: '5px', border: '1px solid #ddd' }}
                      required
                    />
                  </div>
                  
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: 'bold' }}>
                      Geçerlilik Tarihi
                    </label>
                    <input
                      type="date"
                      value={newCoupon.validUntil}
                      onChange={(e) => setNewCoupon({ ...newCoupon, validUntil: e.target.value })}
                      style={{ width: '100%', padding: '10px', borderRadius: '5px', border: '1px solid #ddd' }}
                      required
                    />
                  </div>
                  
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: 'bold' }}>
                      Minimum Alışveriş (TL)
                    </label>
                    <input
                      type="number"
                      min="0"
                      placeholder="100"
                      value={newCoupon.minPurchase}
                      onChange={(e) => setNewCoupon({ ...newCoupon, minPurchase: e.target.value })}
                      style={{ width: '100%', padding: '10px', borderRadius: '5px', border: '1px solid #ddd' }}
                    />
                  </div>
                  
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: 'bold' }}>
                      Maksimum Kullanım
                    </label>
                    <input
                      type="number"
                      min="1"
                      placeholder="999"
                      value={newCoupon.maxUsage}
                      onChange={(e) => setNewCoupon({ ...newCoupon, maxUsage: e.target.value })}
                      style={{ width: '100%', padding: '10px', borderRadius: '5px', border: '1px solid #ddd' }}
                    />
                  </div>
                  
                  {(newCoupon.couponType === 'personal' || newCoupon.couponType === 'comeback') && (
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: 'bold' }}>
                        Hedef Kullanıcı Email
                      </label>
                      <input
                        type="email"
                        placeholder="kullanici@email.com"
                        value={newCoupon.targetUser}
                        onChange={(e) => setNewCoupon({ ...newCoupon, targetUser: e.target.value })}
                        style={{ width: '100%', padding: '10px', borderRadius: '5px', border: '1px solid #ddd' }}
                        required={newCoupon.couponType !== 'general'}
                      />
                    </div>
                  )}
                  
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: 'bold' }}>
                      Açıklama
                    </label>
                    <input
                      type="text"
                      placeholder="Kupon açıklaması"
                      value={newCoupon.description}
                      onChange={(e) => setNewCoupon({ ...newCoupon, description: e.target.value })}
                      style={{ width: '100%', padding: '10px', borderRadius: '5px', border: '1px solid #ddd' }}
                    />
                  </div>
                </div>
                
                {/* Sadece Üyeler & Tek Kullanımlık */}
                <div style={{ display: 'flex', gap: '24px', marginBottom: '15px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px' }}>
                    <input
                      type="checkbox"
                      checked={newCoupon.membersOnly}
                      onChange={(e) => setNewCoupon({ ...newCoupon, membersOnly: e.target.checked })}
                    />
                    Sadece Üyeler (giriş yapmış + email doğrulanmış)
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px' }}>
                    <input
                      type="checkbox"
                      checked={newCoupon.singleUse}
                      onChange={(e) => setNewCoupon({ ...newCoupon, singleUse: e.target.checked })}
                    />
                    Tek Kullanımlık (üye başına 1 kez)
                  </label>
                </div>

                <button
                  type="submit"
                  style={{
                    padding: '12px 30px',
                    backgroundColor: '#667eea',
                    color: 'white',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    fontSize: '16px',
                    fontWeight: 'bold'
                  }}
                >
                  Kupon Oluştur
                </button>
              </form>
            </div>

            {/* Pasif Müşteriler için Özel Kupon */}
            {getInactiveUsers().length > 0 && (
              <div style={{ backgroundColor: '#fff3cd', padding: '20px', borderRadius: '10px', marginBottom: '20px' }}>
                <h3 style={{ color: '#856404' }}>🎯 Geri Kazanım Kampanyası</h3>
                <p style={{ marginBottom: '15px' }}>3 aydan uzun süredir alışveriş yapmayan müşteriler:</p>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  {getInactiveUsers().map(user => (
                    <div key={user.id} style={{
                      backgroundColor: 'white',
                      padding: '10px',
                      borderRadius: '5px',
                      border: '1px solid #ffc107'
                    }}>
                      <p style={{ marginBottom: '5px', fontWeight: 'bold' }}>{user.name}</p>
                      <p style={{ fontSize: '12px', color: '#666', marginBottom: '10px' }}>{user.email}</p>
                      <button
                        onClick={() => generateComebackCoupon(user)}
                        style={{
                          padding: '5px 10px',
                          backgroundColor: '#ffc107',
                          color: 'white',
                          border: 'none',
                          borderRadius: '3px',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        %20 Kupon Oluştur
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Mevcut Kuponlar */}
            <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '10px' }}>
              <h3>Mevcut Kuponlar</h3>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', marginTop: '15px', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #ddd' }}>
                      <th style={{ padding: '10px', textAlign: 'left' }}>Kod</th>
                      <th style={{ padding: '10px', textAlign: 'left' }}>İndirim</th>
                      <th style={{ padding: '10px', textAlign: 'left' }}>Kısıtlama</th>
                      <th style={{ padding: '10px', textAlign: 'left' }}>Geçerlilik</th>
                      <th style={{ padding: '10px', textAlign: 'left' }}>Durum</th>
                      <th style={{ padding: '10px', textAlign: 'left' }}>İşlemler</th>
                    </tr>
                  </thead>
                  <tbody>
                    {coupons.map(coupon => (
                      <tr key={coupon.id} style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: '10px' }}>
                          <strong>{coupon.code}</strong>
                        </td>
                        <td style={{ padding: '10px' }}>%{coupon.discount_percent}</td>
                        <td style={{ padding: '10px' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                            {coupon.members_only && (
                              <span style={{ padding: '2px 6px', borderRadius: '3px', fontSize: '11px', backgroundColor: '#6f42c1', color: 'white', whiteSpace: 'nowrap' }}>
                                Sadece Üye
                              </span>
                            )}
                            {coupon.single_use && (
                              <span style={{ padding: '2px 6px', borderRadius: '3px', fontSize: '11px', backgroundColor: '#fd7e14', color: 'white', whiteSpace: 'nowrap' }}>
                                Tek Kullanım
                              </span>
                            )}
                            {!coupon.members_only && !coupon.single_use && (
                              <span style={{ fontSize: '12px', color: '#999' }}>Genel</span>
                            )}
                          </div>
                        </td>
                        <td style={{ padding: '10px' }}>
                          {new Date(coupon.valid_until).toLocaleDateString('tr-TR')}
                        </td>
                        <td style={{ padding: '10px' }}>
                          <span style={{
                            padding: '3px 8px',
                            borderRadius: '3px',
                            fontSize: '12px',
                            backgroundColor: new Date(coupon.valid_until) > new Date() ? '#28a745' : '#6c757d',
                            color: 'white'
                          }}>
                            {new Date(coupon.valid_until) > new Date() ? 'Aktif' : 'Süresi Dolmuş'}
                          </span>
                        </td>
                        <td style={{ padding: '10px' }}>
                          <button
                            onClick={() => deleteCoupon(coupon.id)}
                            style={{
                              padding: '5px 10px',
                              backgroundColor: '#dc3545',
                              color: 'white',
                              border: 'none',
                              borderRadius: '3px',
                              cursor: 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            Sil
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                
                {coupons.length === 0 && (
                  <p style={{ textAlign: 'center', padding: '20px', color: '#6c757d' }}>
                    Henüz kupon oluşturulmamış
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
        
        {activeTab === 'addProduct' && (
          <div>
            <h1>Ürün Ekleme</h1>

            {/* Yeni Ürün Ekleme Formu */}
            <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '10px', marginBottom: '20px' }}>
              <h3>Yeni Ürün Ekle</h3>
              <form onSubmit={handleAddProduct}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                  <div>
                    <input
                      type="text"
                      placeholder="Ürün Adı"
                      value={newProduct.name}
                      onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })}
                      style={{ padding: '10px', borderRadius: '5px', border: '1px solid #ddd', width: '100%', boxSizing: 'border-box' }}
                      required
                    />
                    {newProduct.name && (
                      <div style={{ fontSize: '11px', color: '#888', marginTop: '4px' }}>
                        🔗 /urun/<strong>{toSlug(newProduct.name)}</strong>
                      </div>
                    )}
                  </div>
                  <input
                    type="number"
                    placeholder="Fiyat (TL)"
                    value={newProduct.price}
                    onChange={(e) => setNewProduct({ ...newProduct, price: e.target.value })}
                    style={{ padding: '10px', borderRadius: '5px', border: '1px solid #ddd' }}
                    required
                  />
                  <div>
                    <input
                      type="number"
                      placeholder="Eski Fiyat (indirim öncesi, opsiyonel)"
                      value={newProduct.compare_at_price}
                      onChange={(e) => setNewProduct({ ...newProduct, compare_at_price: e.target.value })}
                      style={{ padding: '10px', borderRadius: '5px', border: '1px solid #ddd', width: '100%', boxSizing: 'border-box' }}
                    />
                    {newProduct.compare_at_price && Number(newProduct.compare_at_price) <= Number(newProduct.price || 0) && (
                      <div style={{ fontSize: '11px', color: '#e67e22', marginTop: '4px' }}>
                        ⚠️ Eski fiyat, "Fiyat" alanındaki güncel satış fiyatından yüksek olmalı ki indirim gösterilsin.
                      </div>
                    )}
                  </div>
                  <input
                    type="text"
                    placeholder="Açıklama"
                    value={newProduct.description}
                    onChange={(e) => setNewProduct({ ...newProduct, description: e.target.value })}
                    style={{ padding: '10px', borderRadius: '5px', border: '1px solid #ddd' }}
                    required
                  />
                  <input
                    type="number"
                    placeholder="Stok Adedi"
                    value={newProduct.stock}
                    onChange={(e) => setNewProduct({ ...newProduct, stock: e.target.value })}
                    style={{ padding: '10px', borderRadius: '5px', border: '1px solid #ddd' }}
                    required
                  />
                  <select
                    value={newProduct.category}
                    onChange={(e) => {
                      const cat = e.target.value;
                      if (cat === '__yeni__') return;
                      setNewProduct({ ...newProduct, category: cat, subcategory: '' });
                      fetchSubcategories(cat);
                    }}
                    style={{ padding: '10px', borderRadius: '5px', border: '1px solid #ddd' }}
                    required
                  >
                    <option value="">Kategori Seçin</option>
                    {categories.map(c => <option key={c} value={c}>{c}</option>)}
                    <option value="__yeni__">+ Yeni Kategori Ekle</option>
                  </select>
                  {newProduct.category === '__yeni__' || (newCategoryInput !== '' && newProduct.category === '') ? null : null}
                  <div style={{ display: newProduct.category === '__yeni__' ? 'flex' : 'none', gap: '8px' }}>
                    <input
                      type="text"
                      placeholder="Yeni kategori adı (örn: Twilly)"
                      value={newCategoryInput}
                      onChange={e => setNewCategoryInput(e.target.value)}
                      style={{ flex: 1, padding: '10px', borderRadius: '5px', border: '1px solid #ddd' }}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const name = newCategoryInput.trim();
                        if (!name) return;
                        if (!categories.includes(name)) setCategories(prev => [...prev, name]);
                        setNewProduct({ ...newProduct, category: name, subcategory: '' });
                        fetchSubcategories(name);
                        setNewCategoryInput('');
                      }}
                      style={{ padding: '10px 14px', background: '#764ba2', color: '#fff', border: 'none', borderRadius: '5px', cursor: 'pointer', whiteSpace: 'nowrap' }}
                    >
                      Ekle
                    </button>
                  </div>

                  {/* Alt Kategori */}
                  {newProduct.category && (
                    <div>
                      <select
                        value={newProduct.subcategory}
                        onChange={(e) => setNewProduct({ ...newProduct, subcategory: e.target.value })}
                        style={{ padding: '10px', borderRadius: '5px', border: '1px solid #ddd', width: '100%' }}
                      >
                        <option value="">Alt Kategori Seçin (opsiyonel)</option>
                        {subcategories.map(s => (
                          <option key={s.id} value={s.name}>{s.name}</option>
                        ))}
                        <option value="__diger__">+ Yeni Alt Kategori Ekle</option>
                      </select>
                      {newProduct.subcategory === '__diger__' && (
                        <div style={{ display: 'flex', gap: '8px', marginTop: '6px' }}>
                          <input
                            type="text"
                            placeholder={`Yeni alt kategori adı (${newProduct.category} için)`}
                            value={newSubcategoryInput}
                            onChange={(e) => setNewSubcategoryInput(e.target.value)}
                            style={{ flex: 1, padding: '8px', borderRadius: '5px', border: '1px solid #ddd' }}
                          />
                          <button
                            type="button"
                            onClick={async () => {
                              const created = await addSubcategory(newProduct.category, newSubcategoryInput, false);
                              if (created) setNewProduct({ ...newProduct, subcategory: created });
                            }}
                            style={{ padding: '8px 14px', background: '#764ba2', color: '#fff', border: 'none', borderRadius: '5px', cursor: 'pointer', whiteSpace: 'nowrap' }}
                          >
                            Ekle
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  <input
                    type="text"
                    placeholder="Boyut (opsiyonel, örn: 68x68cm)"
                    value={newProduct.size}
                    onChange={(e) => setNewProduct({ ...newProduct, size: e.target.value })}
                    style={{ padding: '10px', borderRadius: '5px', border: '1px solid #ddd' }}
                  />
                  <input
                    type="number"
                    placeholder="Varyant Grup ID (opsiyonel, örn: 1)"
                    value={newProduct.variant_group_id}
                    onChange={(e) => setNewProduct({ ...newProduct, variant_group_id: e.target.value })}
                    style={{ padding: '10px', borderRadius: '5px', border: '1px solid #ddd' }}
                  />
                </div>
                <div style={{ marginTop: '8px', fontSize: '12px', color: '#6c757d' }}>
                  <strong>Boyut/Varyant kullanımı:</strong> Aynı desenin farklı boyutlarını birbirine bağlamak için her ürüne aynı "Varyant Grup ID" değerini girin. Örn: 68x68cm fular ve 55x55cm fular için ikisine de Grup ID: 1 yazın.
                </div>
                
                {/* Resim URL Girişi */}
                <div style={{ marginTop: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                    Ürün Resimleri (URL):
                  </label>
                  <textarea
                    placeholder="Resim URL'lerini virgülle ayırarak girin&#10;Örnek: https://resim1.jpg, https://resim2.jpg&#10;&#10;Ücretsiz resim siteleri:&#10;• https://unsplash.com&#10;• https://pexels.com&#10;• https://pixabay.com"
                    value={newProduct.images}
                    onChange={(e) => handleImageUrlChange(e.target.value)}
                    style={{ 
                      width: '100%', 
                      padding: '10px', 
                      borderRadius: '5px', 
                      border: '1px solid #ddd',
                      minHeight: '100px',
                      resize: 'vertical'
                    }}
                  />
                  
                  {/* Resim Önizleme */}
                  {imagePreview && (
                    <div style={{ marginTop: '15px' }}>
                      <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                        Önizleme:
                      </label>
                      <div style={{ 
                        border: '1px solid #ddd', 
                        borderRadius: '5px', 
                        padding: '10px',
                        textAlign: 'center',
                        backgroundColor: '#f8f9fa'
                      }}>
                        <img 
                          src={imagePreview} 
                          alt="Önizleme" 
                          style={{ 
                            maxWidth: '200px', 
                            maxHeight: '200px',
                            borderRadius: '5px'
                          }}
                          onError={(e) => {
                            e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect width="200" height="200" fill="%23ddd"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%23999"%3EGeçersiz URL%3C/text%3E%3C/svg%3E';
                          }}
                        />
                      </div>
                    </div>
                  )}
                  
                  {/* Örnek URL'ler */}
                  <div style={{ marginTop: '10px', fontSize: '12px', color: '#6c757d' }}>
                    <strong>Örnek İpek Ürün Resimleri (Kopyalayıp kullanabilirsiniz):</strong>
                    <ul style={{ marginTop: '5px', marginLeft: '20px' }}>
                      <li>Eşarp: https://images.unsplash.com/photo-1584917865442-de89df76afd3?w=400</li>
                      <li>Şal: https://images.unsplash.com/photo-1601924994987-69e26d50dc26?w=400</li>
                      <li>Kravat: https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400</li>
                    </ul>
                  </div>
                </div>
                
                <button
                  type="submit"
                  style={{
                    width: '100%',
                    marginTop: '15px',
                    padding: '12px',
                    backgroundColor: '#667eea',
                    color: 'white',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    fontSize: '16px',
                    fontWeight: 'bold'
                  }}
                >
                  Ürün Ekle
                </button>
              </form>
            </div>

          </div>
        )}

        {activeTab === 'productList' && (
          <div>
            <h1>Ürünler</h1>

            {/* Sekme Seçici */}
            <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', flexWrap: 'wrap' }}>
              {[
                { key: 'inStock', label: '📦 Mevcut Ürünler', color: '#28a745', count: products.filter(p => p.is_active !== false && (p.stock || 0) > 0).length },
                { key: 'outOfStock', label: '⚠️ Stoğu Biten Ürünler', color: '#dc3545', count: products.filter(p => p.is_active !== false && (p.stock || 0) === 0).length },
                { key: 'hidden', label: '🙈 Gizlenen Ürünler', color: '#6c757d', count: products.filter(p => p.is_active === false).length },
              ].map(s => (
                <button key={s.key} onClick={() => { setProductSection(s.key); setProductPage(1); }} style={{
                  padding: '10px 20px', borderRadius: '8px', border: '2px solid ' + s.color,
                  backgroundColor: productSection === s.key ? s.color : 'white',
                  color: productSection === s.key ? 'white' : s.color,
                  cursor: 'pointer', fontWeight: 'bold', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px'
                }}>
                  {s.label}
                  <span style={{
                    backgroundColor: productSection === s.key ? 'rgba(255,255,255,0.3)' : s.color,
                    color: 'white', borderRadius: '12px', padding: '1px 8px', fontSize: '12px'
                  }}>{s.count}</span>
                </button>
              ))}
            </div>

            {/* Kategoriye Göre Toplu İndirim */}
            <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '10px', marginBottom: '20px' }}>
              <h3>Kategoriye Göre Toplu İndirim</h3>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                <select
                  value={categoryDiscountCategory}
                  onChange={e => setCategoryDiscountCategory(e.target.value)}
                  style={{ padding: '8px', borderRadius: '5px', border: '1px solid #ddd', fontSize: '14px' }}
                >
                  <option value="">Kategori Seçin</option>
                  {categories.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <input
                  type="number"
                  placeholder="Yüzde (%)"
                  value={categoryDiscountPercent}
                  onChange={e => { setCategoryDiscountPercent(e.target.value); if (e.target.value) setCategoryDiscountPrice(''); }}
                  style={{ padding: '8px', borderRadius: '5px', border: '1px solid #ddd', fontSize: '14px', width: '120px' }}
                />
                <span style={{ color: '#999', fontSize: '13px' }}>veya</span>
                <input
                  type="number"
                  placeholder="Hedef Fiyat (TL)"
                  value={categoryDiscountPrice}
                  onChange={e => { setCategoryDiscountPrice(e.target.value); if (e.target.value) setCategoryDiscountPercent(''); }}
                  style={{ padding: '8px', borderRadius: '5px', border: '1px solid #ddd', fontSize: '14px', width: '140px' }}
                />
                <button
                  type="button"
                  disabled={!categoryDiscountCategory || categoryDiscountLoading || (!categoryDiscountPercent && !categoryDiscountPrice)}
                  onClick={handleApplyCategoryDiscount}
                  style={{ padding: '8px 16px', borderRadius: '5px', border: 'none', backgroundColor: '#667eea', color: 'white', cursor: 'pointer', fontSize: '14px', opacity: (!categoryDiscountCategory || categoryDiscountLoading || (!categoryDiscountPercent && !categoryDiscountPrice)) ? 0.6 : 1 }}
                >
                  Uygula
                </button>
                <button
                  type="button"
                  disabled={!categoryDiscountCategory || categoryDiscountLoading}
                  onClick={handleRemoveCategoryDiscount}
                  style={{ padding: '8px 16px', borderRadius: '5px', border: '1px solid #dc3545', backgroundColor: 'white', color: '#dc3545', cursor: 'pointer', fontSize: '14px', opacity: (!categoryDiscountCategory || categoryDiscountLoading) ? 0.6 : 1 }}
                >
                  Kaldır
                </button>
                {categoryDiscountCategory && (
                  <span style={{ fontSize: '12px', color: '#888' }}>
                    ({products.filter(p => p.category === categoryDiscountCategory).length} ürün)
                  </span>
                )}
              </div>
            </div>

            {/* Ürün Listesi */}
            <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '10px' }}>
              <h3>{productSection === 'inStock' ? 'Mevcut Ürünler' : productSection === 'outOfStock' ? 'Stoğu Biten Ürünler' : 'Gizlenen Ürünler'}</h3>

              {/* Search Paneli */}
              <div style={{ display: 'flex', gap: '10px', marginBottom: '15px', flexWrap: 'wrap', alignItems: 'center' }}>
                <select
                  value={productSearchType}
                  onChange={e => { setProductSearchType(e.target.value); setProductPage(1); }}
                  style={{ padding: '8px', borderRadius: '5px', border: '1px solid #ddd', fontSize: '14px' }}
                >
                  <option value="all">Tümünde Ara</option>
                  <option value="id">ID</option>
                  <option value="variant_group_id">Varyant Grup ID</option>
                  <option value="size">Boyut</option>
                  <option value="name">Ürün Adı</option>
                  <option value="category">Kategori</option>
                </select>
                <input
                  type="text"
                  placeholder="Ara..."
                  value={productSearch}
                  onChange={e => { setProductSearch(e.target.value); setProductPage(1); }}
                  style={{ padding: '8px 12px', borderRadius: '5px', border: '1px solid #ddd', fontSize: '14px', minWidth: '220px' }}
                />
                <select
                  value={productStockSort}
                  onChange={e => { setProductStockSort(e.target.value); setProductPage(1); }}
                  style={{ padding: '8px', borderRadius: '5px', border: '1px solid #ddd', fontSize: '14px' }}
                >
                  <option value="none">Stok Sıralaması Yok</option>
                  <option value="asc">Stok: Azdan Çoğa</option>
                  <option value="desc">Stok: Çoktan Aza</option>
                </select>
                {productSearch && (
                  <button onClick={() => setProductSearch('')} style={{
                    padding: '8px 14px', borderRadius: '5px', border: '1px solid #ddd',
                    backgroundColor: '#f8f9fa', cursor: 'pointer', fontSize: '14px'
                  }}>Temizle</button>
                )}
              </div>

              {/* Seçili Ürünler İçin Toplu İndirim Aracı */}
              {selectedProductIds.length > 0 && (
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center', backgroundColor: '#f8f9fa', padding: '12px', borderRadius: '8px', marginBottom: '15px' }}>
                  <strong style={{ fontSize: '13px' }}>{selectedProductIds.length} ürün seçildi</strong>
                  <input
                    type="number"
                    placeholder="Yüzde (%)"
                    value={bulkDiscountPercent}
                    onChange={e => { setBulkDiscountPercent(e.target.value); if (e.target.value) setBulkDiscountPrice(''); }}
                    style={{ padding: '8px', borderRadius: '5px', border: '1px solid #ddd', fontSize: '14px', width: '110px' }}
                  />
                  <span style={{ color: '#999', fontSize: '13px' }}>veya</span>
                  <input
                    type="number"
                    placeholder="Hedef Fiyat (TL)"
                    value={bulkDiscountPrice}
                    onChange={e => { setBulkDiscountPrice(e.target.value); if (e.target.value) setBulkDiscountPercent(''); }}
                    style={{ padding: '8px', borderRadius: '5px', border: '1px solid #ddd', fontSize: '14px', width: '130px' }}
                  />
                  <button
                    type="button"
                    disabled={bulkDiscountLoading || (!bulkDiscountPercent && !bulkDiscountPrice)}
                    onClick={handleApplySelectedDiscount}
                    style={{ padding: '8px 16px', borderRadius: '5px', border: 'none', backgroundColor: '#667eea', color: 'white', cursor: 'pointer', fontSize: '14px', opacity: (bulkDiscountLoading || (!bulkDiscountPercent && !bulkDiscountPrice)) ? 0.6 : 1 }}
                  >
                    Seçilenlere İndirim Uygula
                  </button>
                  <button
                    type="button"
                    disabled={bulkDiscountLoading}
                    onClick={handleRemoveSelectedDiscount}
                    style={{ padding: '8px 16px', borderRadius: '5px', border: '1px solid #dc3545', backgroundColor: 'white', color: '#dc3545', cursor: 'pointer', fontSize: '14px', opacity: bulkDiscountLoading ? 0.6 : 1 }}
                  >
                    Seçilenlerden İndirimi Kaldır
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelectedProductIds([])}
                    style={{ padding: '8px 12px', borderRadius: '5px', border: '1px solid #ddd', backgroundColor: 'white', color: '#666', cursor: 'pointer', fontSize: '13px' }}
                  >
                    Seçimi Temizle
                  </button>
                </div>
              )}

              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', marginTop: '15px', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #ddd' }}>
                      <th style={{ padding: '10px', textAlign: 'left' }}>
                        <input
                          type="checkbox"
                          checked={paginatedProducts.length > 0 && paginatedProducts.every(p => selectedProductIds.includes(p.id))}
                          onChange={toggleSelectAllProducts}
                          title="Tümünü Seç"
                        />
                      </th>
                      <th style={{ padding: '10px', textAlign: 'left' }}>Resim</th>
                      <th style={{ padding: '10px', textAlign: 'left' }}>ID</th>
                      <th style={{ padding: '10px', textAlign: 'left' }}>Grup ID</th>
                      <th style={{ padding: '10px', textAlign: 'left' }}>Boyut</th>
                      <th style={{ padding: '10px', textAlign: 'left' }}>Ürün Adı</th>
                      <th style={{ padding: '10px', textAlign: 'left' }}>Kategori</th>
                      <th style={{ padding: '10px', textAlign: 'left' }}>Fiyat</th>
                      <th style={{ padding: '10px', textAlign: 'left' }}>Stok</th>
                      <th style={{ padding: '10px', textAlign: 'left' }}>İşlemler</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedProducts.map(product => (
                      <tr key={product.id} style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: '10px' }}>
                          <input
                            type="checkbox"
                            checked={selectedProductIds.includes(product.id)}
                            onChange={() => toggleSelectProduct(product.id)}
                          />
                        </td>
                        <td style={{ padding: '10px' }}>
                          {product.images && product.images.length > 0 ? (
                            <img 
                              src={product.images[0]} 
                              alt={product.name}
                              style={{ 
                                width: '50px', 
                                height: '50px', 
                                objectFit: 'cover',
                                borderRadius: '5px'
                              }}
                              onError={(e) => {
                                e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="50" height="50"%3E%3Crect width="50" height="50" fill="%23ddd"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%23999" font-size="10"%3ENo Image%3C/text%3E%3C/svg%3E';
                              }}
                            />
                          ) : (
                            <div style={{
                              width: '50px',
                              height: '50px',
                              backgroundColor: '#f0f0f0',
                              borderRadius: '5px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '20px'
                            }}>
                              🧣
                            </div>
                          )}
                        </td>
                        <td style={{ padding: '10px' }}>{product.id}</td>
                        <td style={{ padding: '10px' }}>{product.variant_group_id}</td>
                        <td style={{ padding: '10px' }}>{product.size}</td>
                        <td style={{ padding: '10px' }}>{product.name}</td>
                        <td style={{ padding: '10px' }}>{product.category || '-'}</td>
                        <td style={{ padding: '10px' }}>
                          {product.compare_at_price && Number(product.compare_at_price) > Number(product.price) ? (
                            <div>
                              <div style={{ textDecoration: 'line-through', color: '#999', fontSize: '12px' }}>{product.compare_at_price} TL</div>
                              <div style={{ color: '#e74c3c', fontWeight: 'bold' }}>{product.price} TL</div>
                            </div>
                          ) : (
                            <span>{product.price} TL</span>
                          )}
                        </td>
                        <td style={{ padding: '10px' }}>{product.stock || 0}</td>
                        <td style={{ padding: '10px' }}>
                          <button
                            onClick={() => setSelectedProduct(product)}
                            style={{
                              padding: '5px 10px',
                              backgroundColor: '#6c757d',
                              color: 'white',
                              border: 'none',
                              borderRadius: '3px',
                              cursor: 'pointer',
                              marginRight: '5px'
                            }}
                          >
                            Detaylar
                          </button>
                          <button
                            onClick={() => { setEditProduct({ ...product, images: Array.isArray(product.images) ? product.images.join(', ') : (product.images || '') }); fetchEditSubcategories(product.category); setEditNewSubcategoryInput(''); }}
                            style={{
                              padding: '5px 10px',
                              backgroundColor: '#f39c12',
                              color: 'white',
                              border: 'none',
                              borderRadius: '3px',
                              cursor: 'pointer',
                              marginRight: '5px'
                            }}
                          >
                            Düzenle
                          </button>
                          <button
                            onClick={() => toggleProductVisibility(product)}
                            style={{
                              padding: '5px 10px',
                              backgroundColor: product.is_active === false ? '#28a745' : '#6c757d',
                              color: 'white',
                              border: 'none',
                              borderRadius: '3px',
                              cursor: 'pointer',
                              marginRight: '5px'
                            }}
                          >
                            {product.is_active === false ? 'Göster' : 'Gizle'}
                          </button>
                          <button
                            onClick={() => navigate(`/urun/${product.id}`)}
                            style={{
                              padding: '5px 10px',
                              backgroundColor: '#3498db',
                              color: 'white',
                              border: 'none',
                              borderRadius: '3px',
                              cursor: 'pointer',
                              marginRight: '5px'
                            }}
                          >
                            Görüntüle
                          </button>
                          <button
                            onClick={() => deleteProduct(product.id)}
                            style={{
                              padding: '5px 10px',
                              backgroundColor: '#e74c3c',
                              color: 'white',
                              border: 'none',
                              borderRadius: '3px',
                              cursor: 'pointer'
                            }}
                          >
                            Sil
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {productTotalPages > 1 && (
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '6px', marginTop: '20px', flexWrap: 'wrap' }}>
                    <button
                      onClick={() => setProductPage(p => Math.max(1, p - 1))}
                      disabled={productPage === 1}
                      style={{ padding: '6px 12px', borderRadius: '5px', border: '1px solid #ddd', cursor: productPage === 1 ? 'default' : 'pointer', background: productPage === 1 ? '#f5f5f5' : 'white' }}
                    >‹</button>
                    {Array.from({ length: productTotalPages }, (_, i) => i + 1).map(p => (
                      <button key={p} onClick={() => setProductPage(p)} style={{
                        padding: '6px 12px', borderRadius: '5px', border: '1px solid #ddd', cursor: 'pointer',
                        background: p === productPage ? '#764ba2' : 'white',
                        color: p === productPage ? 'white' : '#333',
                        fontWeight: p === productPage ? '700' : '400'
                      }}>{p}</button>
                    ))}
                    <button
                      onClick={() => setProductPage(p => Math.min(productTotalPages, p + 1))}
                      disabled={productPage === productTotalPages}
                      style={{ padding: '6px 12px', borderRadius: '5px', border: '1px solid #ddd', cursor: productPage === productTotalPages ? 'default' : 'pointer', background: productPage === productTotalPages ? '#f5f5f5' : 'white' }}
                    >›</button>
                    <span style={{ fontSize: '13px', color: '#666', marginLeft: '8px' }}>{filteredProducts.length} ürün / Sayfa {productPage}/{productTotalPages}</span>
                  </div>
                )}
              </div>
            </div>

          {/* Ürün Detay Modal */}
          {selectedProduct && (
            <div style={{
              position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
              alignItems: 'center', justifyContent: 'center', zIndex: 1000
            }} onClick={() => setSelectedProduct(null)}>
              <div style={{
                backgroundColor: 'white', borderRadius: '10px', padding: '30px',
                minWidth: '400px', maxWidth: '550px', width: '90%',
                maxHeight: '80vh', overflowY: 'auto'
              }} onClick={e => e.stopPropagation()}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                  <h2 style={{ margin: 0 }}>Ürün Detayı</h2>
                  <button onClick={() => setSelectedProduct(null)} style={{
                    background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#666'
                  }}>✕</button>
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <tbody>
                    {[
                      ['ID', selectedProduct.id],
                      ['Ürün Adı', selectedProduct.name],
                      ['Açıklama', selectedProduct.description || '-'],
                      ['Fiyat', selectedProduct.price ? `${selectedProduct.price} TL` : '-'],
                      ['Stok', selectedProduct.stock ?? '-'],
                      ['Oluşturulma Tarihi', selectedProduct.created_at ? new Date(selectedProduct.created_at).toLocaleString('tr-TR') : '-'],
                      ['Kategori', selectedProduct.category || '-'],
                      ['Boyut', selectedProduct.size || '-'],
                      ['Varyant Grup ID', selectedProduct.variant_group_id || '-'],
                    ].map(([label, value]) => (
                      <tr key={label} style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: '10px 8px', fontWeight: 'bold', color: '#555', width: '45%' }}>{label}</td>
                        <td style={{ padding: '10px 8px' }}>{value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Ürün Düzenleme Modal */}
          {editProduct && (
            <div style={{
              position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
              alignItems: 'center', justifyContent: 'center', zIndex: 1000
            }} onClick={() => setEditProduct(null)}>
              <div style={{
                backgroundColor: 'white', borderRadius: '10px', padding: '30px',
                minWidth: '480px', maxWidth: '600px', width: '90%',
                maxHeight: '85vh', overflowY: 'auto'
              }} onClick={e => e.stopPropagation()}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                  <h2 style={{ margin: 0 }}>Ürün Düzenle <span style={{ color: '#999', fontSize: '16px' }}>#{editProduct.id}</span></h2>
                  <button onClick={() => setEditProduct(null)} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#666' }}>✕</button>
                </div>
                <form onSubmit={handleUpdateProduct}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <label style={{ fontSize: '13px', fontWeight: 'bold', color: '#555' }}>Ürün Adı</label>
                      <input type="text" value={editProduct.name} required
                        onChange={e => setEditProduct({ ...editProduct, name: e.target.value })}
                        style={{ padding: '9px', borderRadius: '5px', border: '1px solid #ddd' }} />
                      {editProduct.name && (
                        <div style={{ fontSize: '11px', color: '#888', marginTop: '3px' }}>
                          🔗 /urun/<strong>{toSlug(editProduct.name)}</strong>
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <label style={{ fontSize: '13px', fontWeight: 'bold', color: '#555' }}>Fiyat (TL)</label>
                      <input type="number" value={editProduct.price} required
                        onChange={e => setEditProduct({ ...editProduct, price: e.target.value })}
                        style={{ padding: '9px', borderRadius: '5px', border: '1px solid #ddd' }} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <label style={{ fontSize: '13px', fontWeight: 'bold', color: '#555' }}>Eski Fiyat (indirim öncesi, opsiyonel)</label>
                      <input type="number" value={editProduct.compare_at_price || ''}
                        onChange={e => setEditProduct({ ...editProduct, compare_at_price: e.target.value })}
                        style={{ padding: '9px', borderRadius: '5px', border: '1px solid #ddd' }} />
                      {editProduct.compare_at_price && Number(editProduct.compare_at_price) <= Number(editProduct.price || 0) && (
                        <div style={{ fontSize: '11px', color: '#e67e22' }}>
                          ⚠️ Eski fiyat, güncel satış fiyatından yüksek olmalı ki indirim gösterilsin.
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <label style={{ fontSize: '13px', fontWeight: 'bold', color: '#555' }}>Stok</label>
                      <input type="number" value={editProduct.stock} required
                        onChange={e => setEditProduct({ ...editProduct, stock: e.target.value })}
                        style={{ padding: '9px', borderRadius: '5px', border: '1px solid #ddd' }} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <label style={{ fontSize: '13px', fontWeight: 'bold', color: '#555' }}>Kategori</label>
                      <select value={editProduct.category || ''} required
                        onChange={e => { const cat = e.target.value; if (cat === '__yeni__') return; setEditProduct({ ...editProduct, category: cat, subcategory: '' }); fetchEditSubcategories(cat); }}
                        style={{ padding: '9px', borderRadius: '5px', border: '1px solid #ddd' }}>
                        <option value="">Seçin</option>
                        {categories.map(c => <option key={c} value={c}>{c}</option>)}
                        <option value="__yeni__">+ Yeni Kategori Ekle</option>
                      </select>
                      {editProduct.category === '__yeni__' && (
                        <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                          <input
                            type="text"
                            placeholder="Yeni kategori adı"
                            value={newCategoryInput}
                            onChange={e => setNewCategoryInput(e.target.value)}
                            style={{ flex: 1, padding: '8px', borderRadius: '5px', border: '1px solid #ddd' }}
                          />
                          <button
                            type="button"
                            onClick={() => {
                              const name = newCategoryInput.trim();
                              if (!name) return;
                              if (!categories.includes(name)) setCategories(prev => [...prev, name]);
                              setEditProduct({ ...editProduct, category: name, subcategory: '' });
                              fetchEditSubcategories(name);
                              setNewCategoryInput('');
                            }}
                            style={{ padding: '8px 14px', background: '#764ba2', color: '#fff', border: 'none', borderRadius: '5px', cursor: 'pointer', whiteSpace: 'nowrap' }}
                          >
                            Ekle
                          </button>
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <label style={{ fontSize: '13px', fontWeight: 'bold', color: '#555' }}>Alt Kategori</label>
                      <select
                        value={editProduct.subcategory || ''}
                        onChange={e => setEditProduct({ ...editProduct, subcategory: e.target.value })}
                        style={{ padding: '9px', borderRadius: '5px', border: '1px solid #ddd' }}
                      >
                        <option value="">Alt Kategori Seçin (opsiyonel)</option>
                        {editSubcategories.map(s => (
                          <option key={s.id} value={s.name}>{s.name}</option>
                        ))}
                        <option value="__diger__">+ Yeni Alt Kategori Ekle</option>
                      </select>
                      {editProduct.subcategory === '__diger__' && (
                        <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                          <input
                            type="text"
                            placeholder={`Yeni alt kategori (${editProduct.category} için)`}
                            value={editNewSubcategoryInput}
                            onChange={e => setEditNewSubcategoryInput(e.target.value)}
                            style={{ flex: 1, padding: '8px', borderRadius: '5px', border: '1px solid #ddd' }}
                          />
                          <button
                            type="button"
                            onClick={async () => {
                              const created = await addSubcategory(editProduct.category, editNewSubcategoryInput, true);
                              if (created) setEditProduct({ ...editProduct, subcategory: created });
                            }}
                            style={{ padding: '8px 14px', background: '#764ba2', color: '#fff', border: 'none', borderRadius: '5px', cursor: 'pointer', whiteSpace: 'nowrap' }}
                          >
                            Ekle
                          </button>
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <label style={{ fontSize: '13px', fontWeight: 'bold', color: '#555' }}>Boyut</label>
                      <input type="text" value={editProduct.size || ''}
                        onChange={e => setEditProduct({ ...editProduct, size: e.target.value })}
                        style={{ padding: '9px', borderRadius: '5px', border: '1px solid #ddd' }} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <label style={{ fontSize: '13px', fontWeight: 'bold', color: '#555' }}>Varyant Grup ID</label>
                      <input type="number" value={editProduct.variant_group_id || ''}
                        onChange={e => setEditProduct({ ...editProduct, variant_group_id: e.target.value })}
                        style={{ padding: '9px', borderRadius: '5px', border: '1px solid #ddd' }} />
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '12px' }}>
                    <label style={{ fontSize: '13px', fontWeight: 'bold', color: '#555' }}>Açıklama</label>
                    <textarea value={editProduct.description || ''} required
                      onChange={e => setEditProduct({ ...editProduct, description: e.target.value })}
                      style={{ padding: '9px', borderRadius: '5px', border: '1px solid #ddd', minHeight: '80px', resize: 'vertical' }} />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '12px' }}>
                    <label style={{ fontSize: '13px', fontWeight: 'bold', color: '#555' }}>Fotoğraf URL'leri (virgülle ayırın)</label>
                    <textarea value={editProduct.images || ''}
                      onChange={e => setEditProduct({ ...editProduct, images: e.target.value })}
                      style={{ padding: '9px', borderRadius: '5px', border: '1px solid #ddd', minHeight: '70px', resize: 'vertical' }} />
                    {/* Mevcut fotoğraf önizlemeleri */}
                    {(() => {
                      const urls = typeof editProduct.images === 'string'
                        ? editProduct.images.split(',').map(u => u.trim()).filter(u => u)
                        : (editProduct.images || []);
                      return urls.length > 0 ? (
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '8px' }}>
                          {urls.map((url, i) => (
                            <img key={i} src={url} alt={`foto-${i+1}`}
                              style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '5px', border: '1px solid #ddd' }}
                              onError={e => { e.target.style.display = 'none'; }} />
                          ))}
                        </div>
                      ) : null;
                    })()}
                  </div>
                  <button type="submit" style={{
                    width: '100%', marginTop: '20px', padding: '12px',
                    backgroundColor: '#f39c12', color: 'white', border: 'none',
                    borderRadius: '5px', cursor: 'pointer', fontSize: '15px', fontWeight: 'bold'
                  }}>
                    Kaydet
                  </button>
                </form>
              </div>
            </div>
          )}
        </div>
        )}

        {/* Kargo Ayarları Tab */}
        {activeTab === 'shipping' && (
          <div>
            <h1>Kargo Ayarları</h1>

            <div style={{ backgroundColor: 'white', padding: '30px', borderRadius: '10px', maxWidth: '800px' }}>
              <form onSubmit={handleSaveShippingSettings}>
                <div style={{ marginBottom: '30px' }}>
                  <h3 style={{ marginBottom: '20px', color: '#333' }}>Genel Kargo Ayarları</h3>

                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 'bold', color: '#555' }}>
                      Kargo Firması
                    </label>
                    <input
                      type="text"
                      value={shippingSettings.carrier}
                      onChange={(e) => setShippingSettings({ ...shippingSettings, carrier: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '12px',
                        borderRadius: '5px',
                        border: '1px solid #ddd',
                        fontSize: '14px'
                      }}
                      placeholder="Örn: Yurtiçi Kargo"
                    />
                  </div>

                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 'bold', color: '#555' }}>
                      Kargo Ücreti (₺)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={shippingSettings.fee}
                      onChange={(e) => setShippingSettings({ ...shippingSettings, fee: parseFloat(e.target.value) || 0 })}
                      style={{
                        width: '100%',
                        padding: '12px',
                        borderRadius: '5px',
                        border: '1px solid #ddd',
                        fontSize: '14px'
                      }}
                      required
                    />
                    <small style={{ color: '#666', fontSize: '12px' }}>
                      Müşterilerden alınacak standart kargo ücreti
                    </small>
                  </div>

                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 'bold', color: '#555' }}>
                      Ücretsiz Kargo Minimum Tutar (₺)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={shippingSettings.freeShippingThreshold}
                      onChange={(e) => setShippingSettings({ ...shippingSettings, freeShippingThreshold: parseFloat(e.target.value) || 0 })}
                      style={{
                        width: '100%',
                        padding: '12px',
                        borderRadius: '5px',
                        border: '1px solid #ddd',
                        fontSize: '14px'
                      }}
                      required
                    />
                    <small style={{ color: '#666', fontSize: '12px' }}>
                      Bu tutarın üzerindeki alışverişlerde kargo ücretsiz olacaktır
                    </small>
                  </div>

                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={shippingSettings.enabled}
                        onChange={(e) => setShippingSettings({ ...shippingSettings, enabled: e.target.checked })}
                        style={{ marginRight: '10px', width: '18px', height: '18px' }}
                      />
                      <span style={{ fontSize: '14px', fontWeight: 'bold', color: '#555' }}>
                        Kargo sistemini aktif et
                      </span>
                    </label>
                    <small style={{ color: '#666', fontSize: '12px', marginLeft: '28px', display: 'block', marginTop: '5px' }}>
                      Bu seçenek kapalıysa tüm siparişlerde kargo ücretsiz olacaktır
                    </small>
                  </div>
                </div>

                <div style={{
                  padding: '20px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px',
                  marginBottom: '20px',
                  border: '1px solid #e9ecef'
                }}>
                  <h4 style={{ marginBottom: '15px', color: '#333', fontSize: '16px' }}>Önizleme</h4>
                  <div style={{ fontSize: '14px', color: '#555', lineHeight: '1.8' }}>
                    <p>
                      <strong>Kargo Firması:</strong> {shippingSettings.carrier || 'Belirtilmedi'}
                    </p>
                    <p>
                      <strong>Kargo Ücreti:</strong> {shippingSettings.enabled ? `₺${shippingSettings.fee.toFixed(2)}` : 'Ücretsiz'}
                    </p>
                    <p>
                      <strong>Ücretsiz Kargo Limiti:</strong> ₺{shippingSettings.freeShippingThreshold.toFixed(2)} ve üzeri
                    </p>
                    <p>
                      <strong>Durum:</strong> {shippingSettings.enabled ? '✅ Aktif' : '❌ Pasif'}
                    </p>
                  </div>
                </div>

                <button
                  type="submit"
                  style={{
                    width: '100%',
                    padding: '15px',
                    backgroundColor: '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    transition: 'background-color 0.3s'
                  }}
                  onMouseOver={(e) => e.target.style.backgroundColor = '#218838'}
                  onMouseOut={(e) => e.target.style.backgroundColor = '#28a745'}
                >
                  💾 Ayarları Kaydet
                </button>
              </form>

              <div style={{ marginTop: '30px', padding: '15px', backgroundColor: '#fff3cd', borderRadius: '8px', border: '1px solid #ffc107' }}>
                <p style={{ margin: 0, fontSize: '13px', color: '#856404' }}>
                  <strong>ℹ️ Bilgi:</strong> Kargo ayarları değiştirildiğinde, tüm sepet ve ödeme sayfalarında yeni değerler kullanılacaktır.
                  Aktif siparişlerdeki kargo ücretleri değişmeyecektir.
                </p>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'users' && (
          <div>
            <h1>Kullanıcı Yönetimi</h1>
            <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '10px' }}>
              <div style={{ marginBottom: '16px' }}>
                <input
                  type="text"
                  placeholder="Ad soyad veya e-posta ile ara..."
                  value={userSearch}
                  onChange={e => setUserSearch(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              <table style={{ width: '100%' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #ddd' }}>
                    <th style={{ padding: '10px', textAlign: 'left' }}>ID</th>
                    <th style={{ padding: '10px', textAlign: 'left' }}>Ad Soyad</th>
                    <th style={{ padding: '10px', textAlign: 'left' }}>Email</th>
                    <th style={{ padding: '10px', textAlign: 'left' }}>Rol</th>
                    <th style={{ padding: '10px', textAlign: 'left' }}>İşlemler</th>
                  </tr>
                </thead>
                <tbody>
                  {users.filter(u => {
                    const q = userSearch.toLowerCase();
                    return (
                      `${u.name} ${u.surname}`.toLowerCase().includes(q) ||
                      u.email.toLowerCase().includes(q)
                    );
                  }).map(user => (
                    <tr key={user.id} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '10px' }}>{user.id}</td>
                      <td style={{ padding: '10px' }}>{user.name} {user.surname}</td>
                      <td style={{ padding: '10px' }}>{user.email}</td>
                      <td style={{ padding: '10px' }}>
                        <span style={{
                          padding: '3px 8px',
                          borderRadius: '3px',
                          backgroundColor: user.role === 'admin' ? '#e74c3c' : '#3498db',
                          color: 'white',
                          fontSize: '12px'
                        }}>
                          {user.role === 'admin' ? 'Yönetici' : 'Kullanıcı'}
                        </span>
                      </td>
                      <td style={{ padding: '10px' }}>
                        <button
                          onClick={() => setSelectedUser(user)}
                          style={{
                            padding: '5px 10px',
                            backgroundColor: '#667eea',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          Detay
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'orders' && (() => {
          const activeOrders = orders.filter(o => !['delivered', 'cancelled', 'refunded'].includes(o.status));
          const pastOrders = orders.filter(o => o.status === 'delivered');
          const cancelledOrders = orders.filter(o => o.status === 'cancelled');
          const refundedOrders = orders.filter(o => o.status === 'refunded');

          const renderOrderTable = (list, showActions) => (
            list.length === 0 ? (
              <p style={{ textAlign: 'center', color: '#6c757d', padding: '20px' }}>Sipariş bulunmuyor.</p>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #e9ecef' }}>
                      <th style={{ padding: '12px', textAlign: 'left' }}>Sipariş No</th>
                      <th style={{ padding: '12px', textAlign: 'left' }}>Müşteri</th>
                      <th style={{ padding: '12px', textAlign: 'left' }}>Tarih</th>
                      <th style={{ padding: '12px', textAlign: 'right' }}>Tutar</th>
                      <th style={{ padding: '12px', textAlign: 'center' }}>Ürün Sayısı</th>
                      <th style={{ padding: '12px', textAlign: 'center' }}>Durum</th>
                      <th style={{ padding: '12px', textAlign: 'center' }}>Kargo</th>
                      <th style={{ padding: '12px', textAlign: 'center' }}>Detay</th>
                      {showActions && <th style={{ padding: '12px', textAlign: 'center' }}>İşlemler</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {list.map(order => (
                      <tr key={order.id} style={{ borderBottom: '1px solid #e9ecef' }}>
                        <td style={{ padding: '12px' }}>
                          <strong style={{ color: '#667eea', fontFamily: 'monospace' }}>
                            {order.order_number}
                          </strong>
                        </td>
                        <td style={{ padding: '12px' }}>
                          <div>{order.user_name}</div>
                          <div style={{ fontSize: '12px', color: '#6c757d' }}>{order.user_email}</div>
                        </td>
                        <td style={{ padding: '12px' }}>
                          {new Date(order.created_at).toLocaleDateString('tr-TR')}
                        </td>
                        <td style={{ padding: '12px', textAlign: 'right' }}>
                          <div style={{ fontWeight: 'bold' }}>₺{Number(order.total_price).toFixed(2)}</div>
                          {order.discount_code && order.discount_amount > 0 && (
                            <div style={{ fontSize: '11px', color: '#28a745', marginTop: '4px' }}>
                              {order.discount_code}: -₺{Number(order.discount_amount).toFixed(2)}
                            </div>
                          )}
                        </td>
                        <td style={{ padding: '12px', textAlign: 'center' }}>{order.item_count} ürün</td>
                        <td style={{ padding: '12px', textAlign: 'center' }}>
                          <span style={{
                            padding: '6px 12px',
                            backgroundColor: getStatusColor(order.status),
                            color: 'white',
                            borderRadius: '15px',
                            fontSize: '12px',
                            fontWeight: 'bold',
                            display: 'inline-block'
                          }}>
                            {getStatusText(order.status)}
                          </span>
                        </td>
                        <td style={{ padding: '12px', textAlign: 'center' }}>
                          {order.tracking_number ? (
                            <div>
                              <div style={{ fontSize: '12px', fontWeight: 'bold' }}>{order.tracking_number}</div>
                              <div style={{ fontSize: '11px', color: '#6c757d' }}>{order.cargo_company}</div>
                            </div>
                          ) : (
                            <span style={{ color: '#6c757d', fontSize: '12px' }}>-</span>
                          )}
                        </td>
                        <td style={{ padding: '12px', textAlign: 'center' }}>
                          <button
                            onClick={() => setSelectedOrderId(order.id)}
                            style={{
                              padding: '6px 12px',
                              backgroundColor: '#667eea',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '12px',
                              fontWeight: '500'
                            }}
                          >
                            📋 Detay
                          </button>
                        </td>
                        {showActions && (
                          <td style={{ padding: '12px', textAlign: 'center' }}>
                            <div style={{ display: 'flex', gap: '5px', justifyContent: 'center', flexWrap: 'wrap' }}>
                              {order.status === 'pending' && (
                                <button onClick={() => handleUpdateOrderStatus(order.id, 'payment_received')}
                                  style={{ padding: '5px 10px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>
                                  Ödeme Onay
                                </button>
                              )}
                              {order.status === 'payment_received' && (
                                <button onClick={() => handleUpdateOrderStatus(order.id, 'preparing')}
                                  style={{ padding: '5px 10px', backgroundColor: '#ffc107', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>
                                  Hazırlanıyor
                                </button>
                              )}
                              {order.status === 'preparing' && (
                                <button onClick={() => handleAddTrackingInfo(order.id)}
                                  style={{ padding: '5px 10px', backgroundColor: '#17a2b8', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>
                                  Kargoya Ver
                                </button>
                              )}
                              {order.status === 'shipped' && (
                                <button onClick={() => handleUpdateOrderStatus(order.id, 'delivered')}
                                  style={{ padding: '5px 10px', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>
                                  Teslim Et
                                </button>
                              )}
                              <button
                                onClick={() => {
                                  if (window.confirm('Siparişi iptal etmek istediğinizden emin misiniz?')) {
                                    handleUpdateOrderStatus(order.id, 'cancelled');
                                  }
                                }}
                                style={{ padding: '5px 10px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>
                                İptal
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          );

          // Filtreleme
          const applyFilters = (list) => {
            return list.filter(order => {
              // Metin filtresi
              if (orderSearch.trim()) {
                const q = orderSearch.toLowerCase();
                const matchOrder = (order.order_number || '').toLowerCase().includes(q);
                const matchCustomer = (order.user_name || '').toLowerCase().includes(q);
                const matchEmail = (order.user_email || '').toLowerCase().includes(q);
                if (orderSearchType === 'order_number' && !matchOrder) return false;
                if (orderSearchType === 'customer' && !matchCustomer) return false;
                if (orderSearchType === 'email' && !matchEmail) return false;
                if (orderSearchType === 'all' && !matchOrder && !matchCustomer && !matchEmail) return false;
              }
              // Tarih filtresi
              if (orderDateMode === 'single' && orderDateSingle) {
                const orderDate = new Date(order.created_at).toISOString().split('T')[0];
                if (orderDate !== orderDateSingle) return false;
              }
              if (orderDateMode === 'range') {
                const orderDate = new Date(order.created_at).toISOString().split('T')[0];
                if (orderDateFrom && orderDate < orderDateFrom) return false;
                if (orderDateTo && orderDate > orderDateTo) return false;
              }
              return true;
            });
          };

          const sections = [
            { key: 'active', label: '🔄 Devam Eden', color: '#007bff', list: applyFilters(activeOrders), actions: true },
            { key: 'past', label: '✅ Geçmiş', color: '#28a745', list: applyFilters(pastOrders), actions: false },
            { key: 'cancelled', label: '❌ İptal Edilen', color: '#dc3545', list: applyFilters(cancelledOrders), actions: false },
            { key: 'refunded', label: '💰 İade Edildi', color: '#17a2b8', list: applyFilters(refundedOrders), actions: false },
          ];

          return (
            <div>
              <h1>Sipariş Yönetimi</h1>

              {/* Arama Paneli */}
              <div style={{ backgroundColor: 'white', padding: '16px 20px', borderRadius: '10px', marginBottom: '16px' }}>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                  {/* Arama tipi */}
                  <div>
                    <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '4px' }}>Ara</div>
                    <select
                      value={orderSearchType}
                      onChange={e => setOrderSearchType(e.target.value)}
                      style={{ padding: '8px 10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px' }}
                    >
                      <option value="all">Tümünde</option>
                      <option value="order_number">Sipariş No</option>
                      <option value="customer">Müşteri Adı</option>
                      <option value="email">E-posta</option>
                    </select>
                  </div>
                  {/* Arama kutusu */}
                  <div style={{ flex: 1, minWidth: '180px' }}>
                    <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '4px' }}>Arama</div>
                    <input
                      type="text"
                      placeholder="Ara..."
                      value={orderSearch}
                      onChange={e => setOrderSearch(e.target.value)}
                      style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }}
                    />
                  </div>
                  {/* Tarih modu */}
                  <div>
                    <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '4px' }}>Tarih Filtresi</div>
                    <select
                      value={orderDateMode}
                      onChange={e => { setOrderDateMode(e.target.value); setOrderDateSingle(''); setOrderDateFrom(''); setOrderDateTo(''); }}
                      style={{ padding: '8px 10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px' }}
                    >
                      <option value="none">Tarih Yok</option>
                      <option value="single">Belirli Tarih</option>
                      <option value="range">Tarih Aralığı</option>
                    </select>
                  </div>
                  {/* Belirli tarih */}
                  {orderDateMode === 'single' && (
                    <div>
                      <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '4px' }}>Tarih</div>
                      <input
                        type="date"
                        value={orderDateSingle}
                        onChange={e => setOrderDateSingle(e.target.value)}
                        style={{ padding: '8px 10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px' }}
                      />
                    </div>
                  )}
                  {/* Tarih aralığı */}
                  {orderDateMode === 'range' && (
                    <>
                      <div>
                        <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '4px' }}>Başlangıç</div>
                        <input
                          type="date"
                          value={orderDateFrom}
                          onChange={e => setOrderDateFrom(e.target.value)}
                          style={{ padding: '8px 10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px' }}
                        />
                      </div>
                      <div>
                        <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '4px' }}>Bitiş</div>
                        <input
                          type="date"
                          value={orderDateTo}
                          onChange={e => setOrderDateTo(e.target.value)}
                          style={{ padding: '8px 10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px' }}
                        />
                      </div>
                    </>
                  )}
                  {/* Temizle butonu */}
                  {(orderSearch || orderDateMode !== 'none') && (
                    <div>
                      <div style={{ fontSize: '12px', color: 'transparent', marginBottom: '4px' }}>-</div>
                      <button
                        onClick={() => { setOrderSearch(''); setOrderSearchType('all'); setOrderDateMode('none'); setOrderDateSingle(''); setOrderDateFrom(''); setOrderDateTo(''); }}
                        style={{ padding: '8px 14px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}
                      >
                        Temizle
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Sekme Başlıkları */}
              <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
                {sections.map(s => (
                  <button
                    key={s.key}
                    onClick={() => setOrderSection(s.key)}
                    style={{
                      padding: '10px 20px',
                      borderRadius: '8px',
                      border: '2px solid ' + s.color,
                      backgroundColor: orderSection === s.key ? s.color : 'white',
                      color: orderSection === s.key ? 'white' : s.color,
                      cursor: 'pointer',
                      fontWeight: 'bold',
                      fontSize: '14px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    {s.label}
                    <span style={{
                      backgroundColor: orderSection === s.key ? 'rgba(255,255,255,0.3)' : s.color,
                      color: 'white',
                      borderRadius: '12px',
                      padding: '1px 8px',
                      fontSize: '12px'
                    }}>
                      {s.list.length}
                    </span>
                  </button>
                ))}
              </div>

              {/* Aktif Sekme İçeriği */}
              {sections.map(s => s.key === orderSection && (
                <div key={s.key} style={{ backgroundColor: 'white', padding: '20px', borderRadius: '10px' }}>
                  {renderOrderTable(s.list, s.actions)}
                </div>
              ))}
            </div>
          );
        })()}

        {/* Email Logs Tab */}
        {activeTab === 'emailLogs' && (() => {
          const q = emailLogSearch.trim().toLowerCase();
          const filteredLogs = emailLogs.filter(log => {
            // Metin filtresi
            if (q) {
              const matchCustomer = `${log.user_name || ''} ${log.user_surname || ''}`.toLowerCase().includes(q);
              const matchEmail = (log.recipient_email || '').toLowerCase().includes(q);
              const matchOrder = (log.order_number || '').toLowerCase().includes(q);
              if (emailLogSearchType === 'customer' && !matchCustomer) return false;
              if (emailLogSearchType === 'email' && !matchEmail) return false;
              if (emailLogSearchType === 'order_number' && !matchOrder) return false;
              if (emailLogSearchType === 'all' && !matchCustomer && !matchEmail && !matchOrder) return false;
            }
            // Tarih filtresi
            if (emailLogDateMode === 'single' && emailLogDateSingle) {
              const logDate = (log.sent_at || '').split('T')[0];
              if (logDate !== emailLogDateSingle) return false;
            }
            if (emailLogDateMode === 'range') {
              const logDate = (log.sent_at || '').split('T')[0];
              if (emailLogDateFrom && logDate < emailLogDateFrom) return false;
              if (emailLogDateTo && logDate > emailLogDateTo) return false;
            }
            return true;
          });

          return (
            <div>
              <h1>Email Logları</h1>
              {/* Search Paneli */}
              <div style={{ backgroundColor: 'white', padding: '16px', borderRadius: '10px', marginBottom: '16px', display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'flex-end' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ fontSize: '12px', color: '#6c757d' }}>Arama Türü</label>
                  <select value={emailLogSearchType} onChange={e => setEmailLogSearchType(e.target.value)}
                    style={{ padding: '8px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '14px' }}>
                    <option value="all">Tümünde Ara</option>
                    <option value="customer">Müşteri Adı</option>
                    <option value="email">E-posta</option>
                    <option value="order_number">Sipariş No</option>
                  </select>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1, minWidth: '180px' }}>
                  <label style={{ fontSize: '12px', color: '#6c757d' }}>Arama</label>
                  <input
                    type="text"
                    placeholder={emailLogSearchType === 'order_number' ? 'Sipariş no...' : emailLogSearchType === 'email' ? 'E-posta...' : emailLogSearchType === 'customer' ? 'Ad soyad...' : 'Ara...'}
                    value={emailLogSearch}
                    onChange={e => setEmailLogSearch(e.target.value)}
                    style={{ padding: '8px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '14px' }}
                  />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ fontSize: '12px', color: '#6c757d' }}>Tarih Filtresi</label>
                  <select value={emailLogDateMode} onChange={e => setEmailLogDateMode(e.target.value)}
                    style={{ padding: '8px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '14px' }}>
                    <option value="none">Tarih Filtresi Yok</option>
                    <option value="single">Belirli Tarih</option>
                    <option value="range">Tarih Aralığı</option>
                  </select>
                </div>
                {emailLogDateMode === 'single' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <label style={{ fontSize: '12px', color: '#6c757d' }}>Tarih</label>
                    <input type="date" value={emailLogDateSingle} onChange={e => setEmailLogDateSingle(e.target.value)}
                      style={{ padding: '8px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '14px' }} />
                  </div>
                )}
                {emailLogDateMode === 'range' && (
                  <>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <label style={{ fontSize: '12px', color: '#6c757d' }}>Başlangıç</label>
                      <input type="date" value={emailLogDateFrom} onChange={e => setEmailLogDateFrom(e.target.value)}
                        style={{ padding: '8px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '14px' }} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <label style={{ fontSize: '12px', color: '#6c757d' }}>Bitiş</label>
                      <input type="date" value={emailLogDateTo} onChange={e => setEmailLogDateTo(e.target.value)}
                        style={{ padding: '8px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '14px' }} />
                    </div>
                  </>
                )}
                <button onClick={() => {
                  setEmailLogSearch('');
                  setEmailLogSearchType('all');
                  setEmailLogDateMode('none');
                  setEmailLogDateSingle('');
                  setEmailLogDateFrom('');
                  setEmailLogDateTo('');
                }} style={{ padding: '8px 16px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px' }}>
                  Temizle
                </button>
              </div>
              <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '10px' }}>
                {filteredLogs.length === 0 ? (
                  <p style={{ textAlign: 'center', color: '#6c757d' }}>
                    {emailLogs.length === 0 ? 'Henüz email gönderimi yapılmamış.' : 'Arama kriterlerine uygun log bulunamadı.'}
                  </p>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <div style={{ marginBottom: '10px', fontSize: '13px', color: '#6c757d' }}>{filteredLogs.length} log gösteriliyor</div>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid #ddd' }}>
                          <th style={{ padding: '12px', textAlign: 'left' }}>Tarih</th>
                          <th style={{ padding: '12px', textAlign: 'left' }}>Tip</th>
                          <th style={{ padding: '12px', textAlign: 'left' }}>Alıcı</th>
                          <th style={{ padding: '12px', textAlign: 'left' }}>Konu</th>
                          <th style={{ padding: '12px', textAlign: 'left' }}>Sipariş No</th>
                          <th style={{ padding: '12px', textAlign: 'center' }}>Durum</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredLogs.map((log) => (
                          <tr key={log.id} style={{ borderBottom: '1px solid #eee' }}>
                            <td style={{ padding: '12px' }}>
                              {new Date(log.sent_at).toLocaleString('tr-TR', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </td>
                            <td style={{ padding: '12px' }}>
                              <span style={{
                                padding: '4px 8px',
                                borderRadius: '4px',
                                fontSize: '12px',
                                backgroundColor: log.email_type === 'order_confirmation' ? '#e3f2fd' : '#fff3e0',
                                color: log.email_type === 'order_confirmation' ? '#1976d2' : '#f57c00'
                              }}>
                                {log.email_type === 'order_confirmation' ? '📨 Sipariş Onayı' :
                                 log.email_type === 'order_shipped' ? '🚚 Kargo Bildirimi' :
                                 log.email_type === 'order_cancelled' ? '❌ İptal Bildirimi' :
                                 log.email_type}
                              </span>
                            </td>
                            <td style={{ padding: '12px' }}>
                              <div>
                                <strong>{log.user_name} {log.user_surname}</strong>
                                <br />
                                <small style={{ color: '#6c757d' }}>{log.recipient_email}</small>
                              </div>
                            </td>
                            <td style={{ padding: '12px', maxWidth: '300px' }}>
                              {log.subject}
                            </td>
                            <td style={{ padding: '12px' }}>
                              {log.order_number || '-'}
                            </td>
                            <td style={{ padding: '12px', textAlign: 'center' }}>
                              <span style={{
                                padding: '4px 12px',
                                borderRadius: '20px',
                                fontSize: '12px',
                                fontWeight: '600',
                                backgroundColor: log.status === 'sent' ? '#d4edda' : log.status === 'failed' ? '#f8d7da' : '#fff3cd',
                                color: log.status === 'sent' ? '#155724' : log.status === 'failed' ? '#721c24' : '#856404'
                              }}>
                                {log.status === 'sent' ? '✅ Gönderildi' :
                                 log.status === 'failed' ? '❌ Başarısız' :
                                 '⏳ Beklemede'}
                              </span>
                              {log.status === 'failed' && log.error_message && (
                                <div style={{
                                  marginTop: '8px',
                                  fontSize: '11px',
                                  color: '#dc3545',
                                  maxWidth: '200px',
                                  wordWrap: 'break-word'
                                }}>
                                  {log.error_message}
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {/* Refund Requests Tab */}
        {activeTab === 'refundRequests' && (() => {
          const q = refundSearch.trim().toLowerCase();
          const filteredRefunds = refundRequests.filter(request => {
            if (q) {
              const matchOrder = (request.order_number || '').toLowerCase().includes(q);
              const matchCustomer = `${request.user_name || ''} ${request.user_surname || ''}`.toLowerCase().includes(q);
              const matchEmail = (request.user_email || '').toLowerCase().includes(q);
              if (refundSearchType === 'order_number' && !matchOrder) return false;
              if (refundSearchType === 'customer' && !matchCustomer) return false;
              if (refundSearchType === 'email' && !matchEmail) return false;
              if (refundSearchType === 'all' && !matchOrder && !matchCustomer && !matchEmail) return false;
            }
            if (refundDateMode === 'single' && refundDateSingle) {
              const reqDate = (request.created_at || '').split('T')[0];
              if (reqDate !== refundDateSingle) return false;
            }
            if (refundDateMode === 'range') {
              const reqDate = (request.created_at || '').split('T')[0];
              if (refundDateFrom && reqDate < refundDateFrom) return false;
              if (refundDateTo && reqDate > refundDateTo) return false;
            }
            return true;
          });

          return (
          <div>
            <h1>İade Talepleri</h1>
            {/* Search Paneli */}
            <div style={{ backgroundColor: 'white', padding: '16px', borderRadius: '10px', marginBottom: '16px', display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'flex-end' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <label style={{ fontSize: '12px', color: '#6c757d' }}>Arama Türü</label>
                <select value={refundSearchType} onChange={e => setRefundSearchType(e.target.value)}
                  style={{ padding: '8px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '14px' }}>
                  <option value="all">Tümünde Ara</option>
                  <option value="order_number">Sipariş No</option>
                  <option value="customer">Müşteri Adı</option>
                  <option value="email">E-posta</option>
                </select>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1, minWidth: '180px' }}>
                <label style={{ fontSize: '12px', color: '#6c757d' }}>Arama</label>
                <input
                  type="text"
                  placeholder={refundSearchType === 'order_number' ? 'Sipariş no...' : refundSearchType === 'email' ? 'E-posta...' : refundSearchType === 'customer' ? 'Ad soyad...' : 'Ara...'}
                  value={refundSearch}
                  onChange={e => setRefundSearch(e.target.value)}
                  style={{ padding: '8px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '14px' }}
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <label style={{ fontSize: '12px', color: '#6c757d' }}>Tarih Filtresi</label>
                <select value={refundDateMode} onChange={e => setRefundDateMode(e.target.value)}
                  style={{ padding: '8px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '14px' }}>
                  <option value="none">Tarih Filtresi Yok</option>
                  <option value="single">Belirli Tarih</option>
                  <option value="range">Tarih Aralığı</option>
                </select>
              </div>
              {refundDateMode === 'single' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ fontSize: '12px', color: '#6c757d' }}>Tarih</label>
                  <input type="date" value={refundDateSingle} onChange={e => setRefundDateSingle(e.target.value)}
                    style={{ padding: '8px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '14px' }} />
                </div>
              )}
              {refundDateMode === 'range' && (
                <>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <label style={{ fontSize: '12px', color: '#6c757d' }}>Başlangıç</label>
                    <input type="date" value={refundDateFrom} onChange={e => setRefundDateFrom(e.target.value)}
                      style={{ padding: '8px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '14px' }} />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <label style={{ fontSize: '12px', color: '#6c757d' }}>Bitiş</label>
                    <input type="date" value={refundDateTo} onChange={e => setRefundDateTo(e.target.value)}
                      style={{ padding: '8px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '14px' }} />
                  </div>
                </>
              )}
              <button onClick={() => {
                setRefundSearch('');
                setRefundSearchType('all');
                setRefundDateMode('none');
                setRefundDateSingle('');
                setRefundDateFrom('');
                setRefundDateTo('');
              }} style={{ padding: '8px 16px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px' }}>
                Temizle
              </button>
            </div>
            <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '10px' }}>
              {filteredRefunds.length === 0 ? (
                <p style={{ textAlign: 'center', color: '#6c757d' }}>
                  {refundRequests.length === 0 ? 'Henüz iade talebi bulunmuyor.' : 'Arama kriterlerine uygun talep bulunamadı.'}
                </p>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <div style={{ marginBottom: '10px', fontSize: '13px', color: '#6c757d' }}>{filteredRefunds.length} talep gösteriliyor</div>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #ddd' }}>
                        <th style={{ padding: '12px', textAlign: 'left' }}>Tarih</th>
                        <th style={{ padding: '12px', textAlign: 'left' }}>Sipariş No</th>
                        <th style={{ padding: '12px', textAlign: 'left' }}>Müşteri</th>
                        <th style={{ padding: '12px', textAlign: 'left' }}>Neden</th>
                        <th style={{ padding: '12px', textAlign: 'right' }}>Tutar</th>
                        <th style={{ padding: '12px', textAlign: 'center' }}>Durum</th>
                        <th style={{ padding: '12px', textAlign: 'center' }}>İşlemler</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredRefunds.map((request) => (
                        <tr key={request.id} style={{ borderBottom: '1px solid #eee' }}>
                          <td style={{ padding: '12px' }}>
                            {new Date(request.created_at).toLocaleDateString('tr-TR')}
                          </td>
                          <td style={{ padding: '12px' }}>
                            <strong>{request.order_number}</strong>
                          </td>
                          <td style={{ padding: '12px' }}>
                            <div>
                              <strong>{request.user_name} {request.user_surname}</strong>
                              <br />
                              <small style={{ color: '#6c757d' }}>{request.user_email}</small>
                            </div>
                          </td>
                          <td style={{ padding: '12px' }}>
                            <div>
                              <strong style={{ color: '#2c3e50' }}>{request.reason}</strong>
                              {request.description && (
                                <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#6c757d' }}>
                                  {request.description}
                                </p>
                              )}
                            </div>
                          </td>
                          <td style={{ padding: '12px', textAlign: 'right', fontWeight: '600' }}>
                            ₺{Number(request.total_price).toFixed(2)}
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center' }}>
                            <span style={{
                              padding: '4px 12px',
                              borderRadius: '20px',
                              fontSize: '12px',
                              fontWeight: '600',
                              backgroundColor:
                                request.status === 'pending' ? '#fff3cd' :
                                request.status === 'approved' ? '#d4edda' :
                                request.status === 'rejected' ? '#f8d7da' :
                                request.status === 'refunded' ? '#d1ecf1' : '#e9ecef',
                              color:
                                request.status === 'pending' ? '#856404' :
                                request.status === 'approved' ? '#155724' :
                                request.status === 'rejected' ? '#721c24' :
                                request.status === 'refunded' ? '#0c5460' : '#495057'
                            }}>
                              {request.status === 'pending' ? '⏳ Beklemede' :
                               request.status === 'approved' ? '✅ Onaylandı' :
                               request.status === 'rejected' ? '❌ Reddedildi' :
                               request.status === 'refunded' ? '💰 İade Edildi' : request.status}
                            </span>
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center' }}>
                            {request.status === 'pending' && (
                              <div style={{ display: 'flex', gap: '5px', justifyContent: 'center' }}>
                                <button
                                  onClick={() => {
                                    const notes = prompt('Admin notu (opsiyonel):');
                                    if (window.confirm('İade talebini onaylamak istediğinizden emin misiniz? Stok geri yüklenecek.')) {
                                      handleUpdateRefundRequest(request.id, 'approved', notes);
                                    }
                                  }}
                                  style={{
                                    padding: '6px 12px',
                                    backgroundColor: '#28a745',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '12px'
                                  }}
                                >
                                  ✓ Onayla
                                </button>
                                <button
                                  onClick={() => {
                                    const notes = prompt('Red nedeni:');
                                    if (notes && window.confirm('İade talebini reddetmek istediğinizden emin misiniz?')) {
                                      handleUpdateRefundRequest(request.id, 'rejected', notes);
                                    }
                                  }}
                                  style={{
                                    padding: '6px 12px',
                                    backgroundColor: '#dc3545',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '12px'
                                  }}
                                >
                                  ✗ Reddet
                                </button>
                              </div>
                            )}
                            {request.status === 'approved' && (
                              <button
                                onClick={() => {
                                  if (window.confirm('İade işlemini tamamlamak istediğinizden emin misiniz?')) {
                                    handleUpdateRefundRequest(request.id, 'refunded', 'İade tamamlandı');
                                  }
                                }}
                                style={{
                                  padding: '6px 12px',
                                  backgroundColor: '#17a2b8',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '12px'
                                }}
                              >
                                💰 İade Et
                              </button>
                            )}
                            {(request.status === 'rejected' || request.status === 'refunded') && (
                              <span style={{ color: '#6c757d', fontSize: '12px' }}>
                                {request.admin_notes || '-'}
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
          );
        })()}
      </div>

      {/* User Detail Modal */}
      {selectedUser && (
        <div
          onClick={() => setSelectedUser(null)}
          style={{
            position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              backgroundColor: 'white', borderRadius: '10px', padding: '30px',
              width: '420px', maxWidth: '90vw', boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, color: '#2c3e50' }}>Kullanıcı Detayı</h2>
              <button
                onClick={() => setSelectedUser(null)}
                style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#6c757d' }}
              >×</button>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                {[
                  ['ID', selectedUser.id],
                  ['Ad', selectedUser.name],
                  ['Soyad', selectedUser.surname],
                  ['E-posta', selectedUser.email],
                  ['Telefon', selectedUser.phone || '-'],
                  ['Rol', selectedUser.role === 'admin' ? 'Yönetici' : 'Kullanıcı'],
                  ['Doğrulandı', selectedUser.verified ? '✅ Evet' : '❌ Hayır'],
                  ['Kayıt Tarihi', selectedUser.created_at ? new Date(selectedUser.created_at).toLocaleString('tr-TR') : '-'],
                ].map(([label, value]) => (
                  <tr key={label} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '10px 8px', fontWeight: 'bold', color: '#555', width: '40%', fontSize: '14px' }}>{label}</td>
                    <td style={{ padding: '10px 8px', color: '#333', fontSize: '14px' }}>{value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Order Details Modal */}
      {selectedOrderId && (
        <OrderDetailsModal
          orderId={selectedOrderId}
          onClose={() => setSelectedOrderId(null)}
        />
      )}
    </div>
  );
}

export default AdminPanel;